import type { SeedIsland } from './seedLatin'

/**
 * The four Latin languages above B1, and two more at A1.
 *
 * Each of them shipped with sixteen islands at A1-B1 and nothing above, which
 * was the same shape Mandarin had: invisible while the level was decoration,
 * and the whole problem once the level decided what a learner is shown. The
 * top half of the control pointed at nothing.
 *
 * Same ten topics in all four, because the product's promise is that the
 * languages are the same course in different languages — and the same
 * escalation as the Mandarin set. At B2 you explain your job past the first
 * answer and tell a story with a shape; at C1 the register carries it; at C2
 * the value is the hedge before the concession and the idiom a learner would
 * never guess.
 *
 * Slugs are in each island's own language, matching the existing set — the ids
 * read as that language's content rather than as German with three
 * translations bolted on.
 */
export const MORE_LATIN: Record<string, SeedIsland[]> = {
  de: [
      {
        slug: 'vorstellen',
        title: 'Introducing yourself',
        nativeTitle: 'Sich vorstellen',
        useWhen:
          'The first thirty seconds with someone new — a colleague, a client, anyone you\'re introduced to and have to greet, name yourself to, account for, and leave politely.',
        tags: ['introductions', 'small talk', 'business travel'],
        level: 1,
        blocks: [
        ['Guten Tag, ich heiße David Miller.', 'Hello — I\'m David Miller.'],
        [
          'Und wie ist Ihr Name?',
          'And your name?',
          'This island stays in the Sie form, the one for anyone you\'ve just met. Sie and its possessive Ihr are always capitalized, and the verb is the plain infinitive — heißen Sie, kommen Sie, sprechen Sie — with one exception you\'ll need immediately: sein becomes Sie sind. "Wie heißen Sie?" is correct too, but "Wie ist Ihr Name?" is the softer thing one adult asks another.',
        ],
        [
          'Freut mich. Ich komme aus Seattle, aus den USA.',
          'Nice to meet you. I\'m from Seattle, in the States.',
          'Freut mich is the reaction to hearing who someone is, so it lands after they\'ve given their name, never before. USA is plural in German and keeps its article: aus den USA, never aus USA — your city needs none, aus Seattle.',
        ],
        [
          'Ich arbeite hier, nur für eine Woche.',
          'I\'m here for work, just for a week.',
          'The nur für eine Woche is what makes this a trip — "Ich arbeite hier" on its own sounds like you work at this place. If you\'re travelling instead: Ich mache hier Urlaub.',
        ],
        ['Ich spreche nur ein bisschen Deutsch.', 'I only speak a little German.'],
        [
          'Auf Wiedersehen, und schönen Tag noch!',
          'Goodbye — have a good day.',
          'Auf Wiedersehen is the safe close at a first meeting and in anything business-like. Tschüss isn\'t reserved for people you know — shop staff and waiters say it all day to customers they address as Sie; it\'s simply casual, so it can land lightly with someone you\'ve only just been introduced to. Schönen Tag noch is a fixed chunk — deliver it whole.',
        ],
        ],
      },
      {
        slug: 'theke',
        title: 'Ordering at the counter',
        nativeTitle: 'An der Theke bestellen',
        useWhen:
          'When you step up to a café or bakery counter and have to order, say how you want it, pay for two people and chase the drink that hasn\'t arrived — all without switching to English.',
        tags: ['ordering', 'cafe', 'paying'],
        level: 1,
        blocks: [
        [
          'Ich nehme einen Kaffee, bitte.',
          'I\'ll have a coffee, please.',
          '"Ich nehme" and "Ich hätte gern" are both what people really say — "Ich hätte gern einen Kaffee" is a shade politer and, at a bakery counter, if anything the more common of the two. Either way der Kaffee becomes einen Kaffee after the verb (accusative).',
        ],
        [
          'Einen großen, bitte, mit Milch.',
          'A large one, please, with milk.',
          'The noun drops out once you\'ve said it, but the ending stays: einen großen. Order a Cola instead and it becomes "eine große".',
        ],
        [
          'Zum Mitnehmen, bitte. Ich bin in Eile.',
          'To go, please. I\'m in a hurry.',
          '"Zum Mitnehmen" is the fixed phrase for takeaway; the counter often asks exactly that, and the other answer is "Nein, ich trinke ihn hier." For being in a rush, Germans say "Ich bin in Eile" — "Ich habe wenig Zeit" sounds like a general complaint about your schedule.',
        ],
        [
          'Und einen Tee für meine Frau, bitte.',
          'And a tea for my wife, please.',
          '"für" always takes the accusative. You can\'t see it on "meine Frau," but with a man it shows: für meinen Mann.',
        ],
        [
          'Alles zusammen, bitte. Ich zahle mit Karte.',
          'All on one bill, please. I\'ll pay by card.',
          'Two different questions come at the till. "Zusammen oder getrennt?" is about one bill or two — the other answer is "Getrennt, bitte." "Bar oder mit Karte?" is about how you pay — the other answer is "Bar, bitte." Saying "mit Karte" up front settles the second one, worth having ready since small counters and bakeries still often want cash.',
        ],
        [
          'Entschuldigung, ich warte noch auf meinen Kaffee.',
          'Excuse me — my coffee still hasn\'t come.',
          '"warten auf" takes the accusative: auf meinen Kaffee. "Entschuldigung" is the right opener for a stranger; "Entschuldige" is the du form and too familiar with staff. The whole island stays on that formal footing.',
        ],
        ],
      },
      {
        slug: 'arbeitdetail',
        title: 'What I do at work',
        nativeTitle: 'Was ich beruflich mache',
        useWhen:
          'When someone asks what you do and the conversation keeps going — who you deal with, what a day looks like, how you ended up there.',
        tags: ['work', 'career', 'introductions'],
        level: 4,
        blocks: [
        [
          'Ich bin bei einem mittelständischen Softwarehaus für die Datenbanken zuständig, also für alles, was passiert, bevor die Kollegen ihre Auswertungen bekommen.',
          'I look after the databases at a mid-sized software company — basically everything that happens before my colleagues get their reports.',
          '"für etwas zuständig sein" is how you say what you own at work; "verantwortlich für" is heavier and implies you\'d carry the blame if it broke. Feel the bracket: "bin" comes second, "zuständig" waits at the very end, and everything else — where you work, then what you handle — stacks up in between.',
        ],
        [
          'Am meisten habe ich mit den Fachabteilungen zu tun, die mir selten sagen können, was sie eigentlich brauchen.',
          'Mostly I deal with the business side, and they can rarely tell me what they actually need.',
          '"mit jemandem zu tun haben" is the everyday phrase for who you deal with. "eigentlich" is doing the eye-roll here — drop it and the line turns into a flat complaint.',
        ],
        [
          'Ein normaler Tag besteht zur Hälfte aus Besprechungen; richtig zum Arbeiten komme ich meistens erst am späten Nachmittag.',
          'Half a normal day is meetings; I don\'t get to any real work until late afternoon.',
          '"erst" here means "not until", never "first" — the false friend that ruins this sentence. "zum Arbeiten kommen" = to get around to actually working.',
        ],
        [
          'Das Anstrengende ist nicht die Technik, sondern dass ich ständig erklären muss, warum etwas länger dauert, als alle gehofft hatten.',
          'The hard part isn\'t the technical side, it\'s having to explain over and over why something is taking longer than everyone hoped.',
          '"sondern" is for a correction that replaces what you just negated — not X, but instead Y, and both cannot be true at once. When the two halves can both stand, you still want "aber": "nicht einfach, aber machbar." Negating first and then correcting is the standard way to head off what people assume your job is.',
        ],
        [
          'Zu dem Job bin ich eher zufällig gekommen: Ich habe Geschichte studiert und bin über ein Praktikum in der IT gelandet.',
          'I fell into the job, really — I studied history and ended up in IT via an internship.',
          '"landen" is what you use for where you ended up without planning it, and it takes sein in the perfect: bin gelandet. If you want the noun, a career-changer is a Quereinsteiger.',
        ],
        [
          'Wenn ich noch einmal anfangen könnte, würde ich das Gleiche machen, nur früher – die Umwege haben mich fast drei Jahre gekostet.',
          'If I could start over I\'d do the same thing, just sooner — the detours cost me nearly three years.',
          'Konjunktiv II in both halves: "könnte" in the wenn-clause, "würde" + infinitive in the main one. With a weak verb like machen the one-word form ("machte") is spelled exactly like the simple past, so würde is the standard choice here, not a shortcut. Save the short form for verbs that have their own: wäre, hätte, käme, könnte.',
        ],
        ],
      },
      {
        slug: 'vergleich',
        title: 'Here vs. back home',
        nativeTitle: 'Hier und bei uns',
        useWhen:
          'When someone asks how life here compares with where you come from and you want to give a real answer — what threw you, what you have absorbed, what still gets you — without declaring a winner.',
        tags: ['living abroad', 'culture', 'opinions'],
        level: 4,
        blocks: [
        [
          'Am meisten überrascht hat mich, wie direkt die Leute hier sind; am Anfang habe ich das schlicht für Unhöflichkeit gehalten.',
          'What surprised me most was how direct people are here — at first I just took it for rudeness.',
        ],
        [
          'Inzwischen habe ich mich so daran gewöhnt, dass mir umgekehrt auffällt, wie viel bei uns um den heißen Brei herumgeredet wird.',
          'By now I\'m so used to it that I notice the opposite — how much we all talk around things back home.',
          'Bei uns is how Germans say back home or where I\'m from; you almost never need in meinem Heimatland, which sounds like a form you\'re filling out. Um den heißen Brei herumreden is to beat around the bush, and it works impersonally in the passive: es wird herumgeredet.',
        ],
        [
          'Was mich dagegen bis heute überfordert, ist der Papierkram: Für jede Kleinigkeit braucht man ein Formular, einen Termin und drei Wochen Geduld.',
          'What still defeats me is the paperwork — every little thing needs a form, an appointment and three weeks\' patience.',
        ],
        [
          'Was mir wirklich fehlt, ist, dass Freunde einfach spontan vorbeikommen; hier meldet man sich lieber vorher an, sonst wirkt man schnell aufdringlich.',
          'What I really miss is friends just dropping by — here you give notice first, or you come across as pushy.',
        ],
        [
          'Ob es hier besser ist, kann ich nicht sagen – es ist anders, und je länger ich hier lebe, desto weniger will ich das überhaupt vergleichen.',
          'I couldn\'t tell you whether it\'s better here — it\'s different, and the longer I live here the less I want to compare the two at all.',
          'Two word orders in one breath. The je-clause sends its verb to the end (je länger ich hier lebe), then desto plus the comparative takes first position and the verb follows immediately (desto weniger *will* ich). Überhaupt is what makes the line sound done with the question rather than merely undecided.',
        ],
        [
          'Ehrlich gesagt merke ich erst hier, wie sehr mich meine Herkunft geprägt hat – auch bei Dingen, über die ich früher nie nachgedacht habe.',
          'Honestly, it\'s only here that I notice how much where I\'m from has shaped me — even in things I never used to think about.',
          'Indicative habe, not hätte. German states a non-event flat: ich habe nie darüber nachgedacht. Konjunktiv II would turn it into things I\'d never have thought about, a hypothetical you haven\'t set up. And nachdenken takes über plus the accusative, so it is über die — never über denen.',
        ],
        ],
      },
      {
        slug: 'geschichte',
        title: 'A small disaster',
        nativeTitle: 'Eine kleine Katastrophe',
        useWhen:
          'When someone asks how your week went and the honest answer is a story about the thing that went wrong.',
        tags: ['storytelling', 'past', 'mishap'],
        level: 4,
        blocks: [
        [
          'Vor ein paar Wochen wollte ich nur schnell den Müll runterbringen und habe dabei die Wohnungstür einfach hinter mir zufallen lassen.',
          'A few weeks ago I was just taking the trash down, and I let the apartment door fall shut behind me.',
          'The whole island is casual spoken German — runterbringen, not the written herunterbringen. Watch the Perfekt: lassen plus another verb gives a double infinitive, habe … zufallen lassen, never gelassen.',
        ],
        [
          'Erst unten im Hof ist mir aufgefallen, dass mein Schlüssel und mein Handy beide oben auf dem Küchentisch lagen.',
          'It wasn\'t until I got down to the courtyard that I realized my keys and my phone were both up on the kitchen table.',
          'erst here is "not until", not "first" — Erst unten im Hof … is the whole "only once I was downstairs did it hit me" effect in one word. And mir fällt etwas auf is how the realization lands: it happens to you, so you are dative.',
        ],
        [
          'Ich bin ehrlich gesagt völlig in Panik geraten, weil ich eine Stunde später einen Termin hatte, zu dem ich auf keinen Fall zu spät kommen durfte.',
          'Honestly, I completely panicked, because I had an appointment an hour later that I absolutely could not be late for.',
        ],
        [
          'Zum Glück war meine Nachbarin zu Hause und hat mir ihr Handy geliehen, aber der Schlüsseldienst hat mich am Ende hundertfünfzig Euro gekostet.',
          'Luckily my neighbor was home and lent me her phone, but the locksmith ended up costing me a hundred and fifty euros.',
        ],
        [
          'Hätte ich meiner Nachbarin vorher einen Zweitschlüssel gegeben, wäre mir der ganze Ärger erspart geblieben – aber daran denkt man ja vorher nie.',
          'If I\'d given my neighbor a spare key beforehand, I\'d have been spared the whole mess — but you never think of that in advance.',
          'Drop the wenn and the verb comes first: Hätte ich …, wäre … — no wenn, and no verb stranded at the end. The neutral everyday form is Wenn ich meiner Nachbarin vorher einen Zweitschlüssel gegeben hätte, …; the verb-first version is tighter and a shade more literary. Drill it anyway — it is completely alive in speech in exactly this rueful-hindsight shape (Hätte ich das mal vorher gewusst …).',
        ],
        [
          'Seitdem liegt ein Zweitschlüssel bei meiner Nachbarin, und ich schaue jedes Mal nach, ob ich den Schlüssel dabeihabe, bevor ich die Tür zumache.',
          'Now there\'s a spare key at my neighbor\'s, and I check every single time that I\'ve got my key on me before I close the door.',
          'nachschauen takes an indirect yes/no question — nachschauen, ob, never nachschauen, dass. (sich vergewissern, dass would be grammatical but lands a register up, near paperwork German.)',
        ],
        ],
      },
      {
        slug: 'druck',
        title: 'Work pressure',
        nativeTitle: 'Druck im Job',
        useWhen:
          'When someone asks how work is going and you want to answer honestly – hedged, understated, adult – instead of saying "ganz gut" and changing the subject.',
        tags: ['work', 'stress', 'opinions'],
        level: 5,
        blocks: [
        [
          'Jammern will ich nicht, aber im Moment ist es schon ziemlich grenzwertig.',
          'I don\'t want to complain, but right now it\'s pretty close to the edge.',
          'Fronting the infinitive – Jammern will ich nicht – is the idiomatic move, and it keeps the conjugated verb in second place. The unstressed schon isn\'t "already"; it concedes the point: it is, admittedly, pretty bad. Nothing in this island addresses the listener directly, so the whole set works unchanged in a du- or a Sie-conversation.',
        ],
        [
          'Es sind gar nicht mal die Stunden – es ist eher so, dass ich abends nicht mehr richtig abschalte.',
          'It\'s not really the hours – it\'s more that I can\'t switch off in the evening.',
          'gar nicht mal softens a denial into "it\'s not so much…" rather than a flat contradiction. abschalten is the same verb used for switching off a device, which is exactly the image.',
        ],
        [
          'Auf dem Papier haben wir Gleitzeit, in der Praxis wird trotzdem erwartet, dass man abends um zehn noch auf Mails reagiert.',
          'On paper we have flexible hours; in practice you\'re still expected to answer email at ten at night.',
        ],
        [
          'Inzwischen lasse ich das Diensthandy am Wochenende bewusst liegen – und bisher ist die Welt deswegen nicht untergegangen.',
          'These days I deliberately leave my work phone alone on weekends – and so far the world hasn\'t ended.',
        ],
        [
          'Das Unangenehme daran ist ja, dass ich mir einen guten Teil des Drucks selbst mache.',
          'The uncomfortable part is that a good chunk of the pressure is self-inflicted.',
          'sich Druck machen takes a dative reflexive: mir, not mich. The particle ja flags the point as something the listener will recognize straight away – it invites agreement instead of arguing.',
        ],
        [
          'Von Work-Life-Balance halte ich ehrlich gesagt wenig – mir würde schon reichen, wenn ich sonntags nicht ständig an die Arbeit denken müsste.',
          'Honestly, I don\'t think much of "work-life balance" – I\'d settle for not thinking about work all day Sunday.',
          'von etwas wenig halten is how you dismiss an idea. Don\'t calque "think much of": Ich denke wenig von Work-Life-Balance is not German – an opinion about a thing takes halten von, or a full clause with denken über (Was denkst du über Gleitzeit?). German does use von with denken, but for your opinion of a person: Was sollen die Leute von mir denken? Here schon reichen is understatement – that alone would be plenty. The English loanword is common in German offices and usually carries a faint whiff of the HR brochure, which is why the line can push back on it.',
        ],
        ],
      },
      {
        slug: 'technik',
        title: 'AI and my work',
        nativeTitle: 'KI in meinem Beruf',
        useWhen:
          'When the talk turns to whether AI is coming for your job — over dinner, after a meeting, with colleagues — and you want to say something more considered than yes or no.',
        tags: ['work', 'ai', 'opinions'],
        level: 5,
        blocks: [
        [
          'Ehrlich gesagt sehe ich das nüchterner, als es meist dargestellt wird.',
          'Honestly, I take a cooler view of it than you usually hear.',
        ],
        [
          'In meinem Bereich nimmt mir die KI inzwischen einen Großteil der Routinearbeit ab – und darüber bin ich alles andere als unglücklich.',
          'In my field AI now takes a lot of the routine off my plate, and I\'m not the least bit sorry about that.',
          'A dative pronoun goes ahead of a full-noun subject: nimmt *mir die KI* … ab, not nimmt die KI mir ab. jemandem etwas abnehmen is the everyday verb for taking work off someone\'s hands.',
        ],
        [
          'Was sie mir nicht abnimmt, ist die Verantwortung – vor allem dann, wenn sich eine Entscheidung im Nachhinein als falsch herausstellt.',
          'What it doesn\'t take off my plate is the responsibility — especially when a decision turns out later to have been the wrong one.',
          'Was …, ist … is the standard German cleft for setting one thing against another, and it reuses abnehmen from the line before — that echo is what makes the two lines sound like one thought.',
        ],
        [
          'Letztlich steht und fällt es mit dem Urteilsvermögen, und das lässt sich nun einmal schlecht automatisieren.',
          'Ultimately it comes down to judgment, and that\'s the part you just can\'t automate.',
          'sich lassen + infinitive replaces a clumsy passive: das lässt sich schlecht automatisieren = that can hardly be automated. nun einmal (spoken: nun mal) is the shrug — that\'s simply how it is.',
        ],
        [
          'Ob das in zehn Jahren noch gilt, möchte ich allerdings nicht beschwören.',
          'Whether that still holds in ten years, though, I wouldn\'t swear to.',
        ],
        [
          'Mir wäre ohnehin lieber, die Debatte würde sich weniger ums Ersetzen drehen und mehr darum, wer am Ende dafür geradestehen muss.',
          'Personally I\'d rather the debate were less about replacement and more about who has to answer for it in the end.',
          'geradestehen für etwas = to answer for something; one word in this sense (two words, gerade stehen, is literally standing up straight). The Konjunktiv II — wäre lieber, würde sich drehen — keeps it a preference rather than a demand.',
        ],
        ],
      },
      {
        slug: 'geschaeft',
        title: 'Running a meeting',
        nativeTitle: 'Eine Besprechung leiten',
        useWhen:
          'When you have to chair a German-language work meeting and hold your own on scope and deadlines without sounding blunt or evasive.',
        tags: ['business', 'meetings', 'politeness'],
        level: 5,
        blocks: [
        [
          'Sofern es keine Einwände gibt, würde ich gleich zum ersten Tagesordnungspunkt kommen.',
          'Opening the meeting when you\'re the one running it.',
          'Sie throughout this island — anything else in a German meeting is a decision about the relationship, not a style choice. The würde/wäre that runs through these lines is politeness, not hypothesis: "Ich komme jetzt zum ersten Punkt" is correct German but lands as an instruction. On a written agenda the Tagesordnungspunkte are numbered TOP 1, TOP 2, and people say "TOP eins" out loud.',
        ],
        [
          'Mein Vorschlag wäre, das Ganze in zwei Phasen aufzuteilen.',
          'Putting a proposal on the table without sounding like it\'s already decided.',
        ],
        [
          'Ehrlich gesagt halte ich den Zeitplan für ziemlich sportlich.',
          'Flagging that the timeline is far too tight, without calling it impossible.',
          'sportlich, said of a schedule or a budget, is standard office irony for "far too tight". unrealistisch or nicht machbar would be a verdict and ends the discussion; sportlich hands the problem back across the table and leaves them room to move.',
        ],
        [
          'Machbar wäre das aus meiner Sicht nur, wenn wir den Umfang entsprechend zurückfahren.',
          'Offering the trade: they can have the date or the full scope.',
        ],
        [
          'Da würde ich mich ungern festlegen, bevor ich das intern abgestimmt habe.',
          'Declining to commit on the spot, without stalling outright.',
          'ungern carries the whole refusal — literally "reluctantly", in practice "I\'m not going to". Etwas intern abstimmen is the fixed phrase for clearing something with your own side; über etwas abstimmen means to put it to a vote, which is not what you are offering.',
        ],
        [
          'Dann würde ich es für heute dabei belassen – das Protokoll geht Ihnen bis morgen Mittag zu.',
          'Ending the meeting and saying what lands in their inbox.',
          'Etwas geht Ihnen zu (dative) is formal business German for "you\'ll receive it" — a clear register above ich schicke es Ihnen, and the natural verb for minutes, offers and contracts. Es dabei belassen is the set phrase for calling time on a discussion.',
        ],
        ],
      },
      {
        slug: 'standpunkt',
        title: 'Holding your ground',
        nativeTitle: 'Zugeben, ohne nachzugeben',
        useWhen:
          'When a conversation turns to something you actually have a view on — politics, a hiring decision, how the team should work — and you want to disagree with German colleagues without either backing down or sounding like you haven\'t heard them.',
        tags: ['debate', 'hedging', 'opinions'],
        level: 6,
        blocks: [
        [
          'Ihr Einwand ist nicht von der Hand zu weisen – da bin ich ganz bei Ihnen.',
          'You\'ve got a real point there, and I\'m completely with you on that one.',
          'nicht von der Hand zu weisen is fixed litotes ("not to be waved aside") and concedes a lot without ever saying you agree. Da bin ich ganz bei Ihnen is the current phrase for standing with someone on exactly one point — the stressed *da* is what limits it. The island holds Sie throughout; in du it\'s da bin ich ganz bei dir.',
        ],
        [
          'Das mag alles stimmen, nur ziehe ich daraus einen ganz anderen Schluss.',
          'That may all be true — I just draw a completely different conclusion from it.',
          'The pivot is nur, not aber: aber fights the point, nur lets it stand and moves the disagreement one step back, onto what follows from it. Used this way it triggers inversion — nur *ziehe* ich, never nur ich ziehe.',
        ],
        [
          'Das Argument ist ja nicht falsch – es greift mir bloß zu kurz.',
          'The argument isn\'t wrong, exactly — it just doesn\'t get far enough for me.',
          'zu kurz greifen faults an argument without calling it wrong: it holds, it simply doesn\'t reach. The dative mir makes it your judgement rather than a verdict — drop it and the line turns noticeably sharper. Unstressed ja marks the concession as something you both already knew.',
        ],
        [
          'Ob sich das am Ende auch durchsetzen lässt, steht noch einmal auf einem ganz anderen Blatt.',
          'Whether it would ever actually get anywhere is a whole separate question.',
          'auf einem anderen Blatt stehen fences off a side issue before the other side can win the whole argument on it. noch einmal isn\'t "again" here — it stacks distance: one more question beyond the one you\'re having.',
        ],
        [
          'Unterm Strich halte ich es trotzdem für das kleinere Übel – bei allem, was dagegen spricht.',
          'On balance I still think it\'s the lesser evil, and I\'m counting everything that speaks against it.',
          'The trailing bei allem, was dagegen spricht hands the entire opposing case back in one clause and keeps the conclusion anyway — you concede the evidence, not the point. Unterm Strich is the contracted unter dem, the bookkeeping image where English says on balance.',
        ],
        [
          'Ich lasse mich gern eines Besseren belehren – überzeugt hat mich bisher allerdings kein Gegenargument.',
          'I\'m genuinely happy to be proved wrong — though so far nothing anyone\'s said has managed it.',
          'sich eines Besseren belehren lassen is fixed and takes the genitive. It sounds like an open door; in practice it hands the burden of proof back, and everyone in the room hears that. Fronting the participle (überzeugt hat mich …) is normal spoken emphasis, not literary word order.',
        ],
        ],
      },
      {
        slug: 'redewendung',
        title: 'Idioms under pressure',
        nativeTitle: 'Wenn der Wurm drin ist',
        useWhen:
          'When a project you are on is visibly going sideways and you want to say so the way your German colleagues do — in the idiom, and at the right level of bluntness.',
        tags: ['idioms', 'work', 'register'],
        level: 6,
        blocks: [
        [
          'Bei dem Projekt ist von Anfang an der Wurm drin.',
          'That project has been going wrong from the start.',
          'Says a run of small failures, not one catastrophe — which is why it lands as a diagnosis rather than an accusation and is safe in a meeting. „drin“ is fixed; nobody says „darin“ here.',
        ],
        [
          'Wir haben die Entscheidung viel zu lange auf die lange Bank geschoben.',
          'We put the decision off way too long.',
          'Stylistically neutral and standard in written German — this one goes into an email to management without a second thought. The „lange Bank“ was the chest in a medieval courtroom where case files got shelved, so the image is filing something away, not sitting on it.',
        ],
        [
          'Und jetzt haben wir den Salat.',
          'And now look at the mess.',
          'Announces that the mess everyone saw coming has landed — not a burden you are saddled with long-term; for that a German says „damit müssen wir jetzt leben“. Colloquial and resigned with a shrug about your own side; aimed at someone else it is pure I-told-you-so. Canonical variant: „Da haben wir den Salat.“ Save it for colleagues, not clients.',
        ],
        [
          'Ehrlich gesagt ist da noch reichlich Luft nach oben.',
          'Honestly, it\'s nowhere near good enough.',
          'Office understatement, and everyone hears it as understatement: German colleagues reach for this precisely when the result is bad. Praise-shaped on the surface, so it survives being forwarded.',
        ],
        [
          'Ich will niemandem auf den Schlips treten, aber so wird das nichts.',
          'I don\'t want to step on anyone\'s toes, but this isn\'t going to work.',
          '„Schlips“ is a necktie — the image is deliberately comic, and that mildness is what licenses how flat the second half is. Leave that second half bare: „So wird das halt nichts“ is perfectly good German, but you have already spent your hedge in the first clause, and a second one drains the line.',
        ],
        [
          'Wenn sich bis Freitag nichts tut, ziehe ich die Reißleine.',
          'If nothing moves by Friday, I\'m pulling the plug.',
          'The Reißleine is a parachute ripcord: an emergency stop you choose yourself, not a failure, which is why managers say it about their own projects. Impersonal „sich tun“ („es tut sich was“, „es tut sich nichts“) is the tightest verb for movement in a situation; „wenn bis Freitag nichts passiert“ is fine too, just flatter.',
        ],
        ],
      },
  ],
  fr: [
      {
        slug: 'presenter',
        title: 'Introducing yourself',
        nativeTitle: 'Se présenter',
        useWhen:
          'The first thirty seconds with anyone new — a colleague, a host, someone at the next table — when you need to give your name, get theirs, say where you are from and why you are here, and close politely.',
        tags: ['introductions', 'greetings', 'travel'],
        level: 1,
        blocks: [
        [
          'Bonjour, je m\'appelle David Miller. Enchanté.',
          'Hello, I\'m David Miller. Nice to meet you.',
          'Enchanté goes with the handshake, not before it. A woman writes enchantée — the -e is silent, so the two sound identical.',
        ],
        [
          'Et vous, comment vous appelez-vous ?',
          'And you — what\'s your name?',
          'Every line here is vous. With someone you have just met, tu is the wrong register; you wait to be offered it. Note the space before the question mark — French punctuation takes one.',
        ],
        [
          'Je parle un peu français, mais pas très bien.',
          'I speak a little French, but not very well.',
        ],
        [
          'Je suis américain. Je viens de Seattle.',
          'I\'m American. I\'m from Seattle.',
          'After être, nationalities and jobs take no article: je suis américain, je suis ingénieur — never je suis un américain. Lowercase as an adjective; a woman says américaine.',
        ],
        [
          'Je suis ici pour le travail. Je reste deux semaines.',
          'I\'m here for work — I\'m staying two weeks.',
          'Rester takes the length bare: je reste deux semaines, no preposition. Said in one breath it would be pour deux semaines — the length of stay you are planning. Pendant deux semaines is a stretch you are inside and does not go with je suis ici.',
        ],
        [
          'Merci beaucoup. Au revoir, bonne journée !',
          'Thanks very much. Goodbye, have a good day.',
          'Bonne journée while the day is still going, bonne soirée from about six. Bonne nuit is only for someone heading to bed.',
        ],
        ],
      },
      {
        slug: 'comptoir',
        title: 'Ordering a drink',
        nativeTitle: 'Commander au comptoir',
        useWhen:
          'You\'re at a café or bakery counter and have to order, answer the size and takeaway questions, add a drink for a friend, pay for both, and ask where yours has got to.',
        tags: ['cafe', 'ordering', 'paying'],
        level: 1,
        blocks: [
        [
          'Bonjour ! Je voudrais un café, s\'il vous plaît.',
          'Open the order — greeting first, then what you want.',
          'Bonjour is not optional in France; walking up and starting with the order reads as rude. Use je voudrais, not je veux, which lands like a demand. The whole island stays on vous.',
        ],
        [
          'Un grand, s\'il vous plaît.',
          'Pick the size when they ask you.',
          'You\'ll hear it as « Un grand ou un petit ? » — answer with just the size, no need to name the drink again. When you do name it, the size goes in front: un grand crème. Note that in a traditional café « un café » is an espresso and comes one size; the question belongs to un crème or un allongé, and to bakery and chain counters.',
        ],
        [
          'À emporter, s\'il vous plaît.',
          'Answer the staying-or-going question.',
          'You\'ll hear it first: « Sur place ou à emporter ? » — answer with just the half you want, not a full sentence. Sur place means staying, and in a traditional café sitting at a table can cost more than standing at the counter.',
        ],
        [
          'Et un thé pour mon amie, s\'il vous plaît.',
          'Add a drink for the person you\'re with.',
          'Mon amie, not ma amie — mon goes before any vowel sound, even on a feminine noun. Starting with et keeps it on the same order instead of opening a new one.',
        ],
        [
          'Je paie les deux, par carte.',
          'Say you\'re covering both drinks, and how you\'re paying.',
          'If they ask « Ensemble ou séparément ? », « Ensemble » on its own is the whole answer. Small cafés and bakeries often set a card minimum, so keep a couple of euros on you.',
        ],
        [
          'Excusez-moi, j\'attends toujours mon café.',
          'Chase the drink when it hasn\'t come.',
          'Toujours here means still, not always: j\'attends toujours is "I\'m still waiting." Excusez-moi in front keeps it a reminder rather than a complaint.',
        ],
        ],
      },
      {
        slug: 'metier',
        title: 'What I actually do at work',
        nativeTitle: 'Mon métier, concrètement',
        useWhen:
          'When someone asks what you do for a living and keeps asking — who you deal with, what a day looks like, how you got there.',
        tags: ['work', 'conversation', 'self-introduction'],
        level: 4,
        blocks: [
        [
          'Concrètement, je m\'occupe de tout ce qui touche à la relation client : c\'est moi qui suis en première ligne dès que ça coince.',
          'Basically, I handle anything to do with the client relationship — I\'m the first one they come to when something goes wrong.',
          'In the cleft *c\'est moi qui…*, the verb agrees with *moi*: **suis**, never *c\'est moi qui est*. And *ça coince* is the everyday way to say something has jammed up.',
        ],
        [
          'Je travaille surtout avec les équipes techniques, mais il faut que je fasse le lien avec le service commercial presque tous les jours.',
          'I mostly work with the technical teams, but I have to coordinate with sales pretty much every day.',
        ],
        [
          'Une journée type ? Je commence par faire le tri dans mes mails, ensuite j\'enchaîne les réunions, et le vrai travail commence en fin d\'après-midi.',
          'A typical day? I start by clearing out my inbox, then it\'s back-to-back meetings, and the real work starts late afternoon.',
          '*Enchaîner* is doing things back to back with no gap — *j\'enchaîne les réunions, les rendez-vous, les trajets*. Note the space before the question mark; French punctuation wants it.',
        ],
        [
          'Je n\'ai pas fait d\'études pour ça : j\'ai commencé comme stagiaire et, de fil en aiguille, on m\'a confié de vrais dossiers.',
          'I didn\'t train for this — I started as an intern, one thing led to another, and eventually they handed me real cases of my own.',
        ],
        [
          'Ce qui me plaît, c\'est que personne ne me dit comment m\'y prendre ; en revanche, quand ça dérape, c\'est moi qui assume.',
          'What I like is that nobody tells me how to go about it; the flip side is that when it goes off the rails, I\'m the one who carries it.',
          '*Assumer* used with no object means taking the consequences and not passing them on — *j\'assume* is what you say about a decision of yours that went badly. The false friend is only the "I assume that…" sense: that is *je suppose*. In its other English sense *assumer* lines up fine — *assumer ses responsabilités*, *assumer un rôle*.',
        ],
        [
          'Les gens s\'imaginent que je passe mes journées devant un écran, alors qu\'en réalité je passe le plus clair de mon temps à négocier.',
          'People picture me sitting in front of a screen all day, when actually I spend most of my time negotiating.',
          '*Le plus clair de mon temps* is fixed and has nothing to do with clarity — it just means most of it. *La plupart de mon temps* is not an option: with a possessive, *la plupart* wants a plural (*la plupart de mes journées*). The one singular it does take is the set phrase *la plupart du temps*, and that means "most of the time," not "most of my time."',
        ],
        ],
      },
      {
        slug: 'comparaison',
        title: 'Here vs. back home',
        nativeTitle: 'Ici et là-bas',
        useWhen:
          'When someone asks how life here compares with where you\'re from, and you want to answer with something better than \\"it\'s different.\\"',
        tags: ['expat life', 'culture shock', 'opinions'],
        level: 4,
        blocks: [
        [
          'Ce qui m\'a le plus surpris quand je suis arrivé, c\'est que tout ferme à midi et que personne ne trouve ça bizarre.',
          'The thing that surprised me most when I arrived was that everything shuts at lunchtime and nobody thinks that\'s odd.',
          'The frame is Ce qui…, c\'est que… — the c\'est carries the sentence and cannot be dropped; "Ce qui m\'a surpris était que" is not French. A woman says surprise… arrivée.',
        ],
        [
          'Au bout de deux ans, je me suis habitué à dîner à huit heures ; maintenant, quand je rentre au pays, je trouve qu\'on mange beaucoup trop tôt.',
          'Two years in, eating at eight feels normal — now when I go back home I think everyone eats far too early.',
          'rentrer au pays is the set phrase for going back to the country you\'re from; bare je rentre is heard as "when I get in", from work. A woman says je me suis habituée.',
        ],
        [
          'L\'administration, en revanche, je ne m\'y ferai jamais : il manque toujours un papier dont personne ne vous avait parlé.',
          'The bureaucracy, though — that I\'ll never get used to. There\'s always one more form nobody warned you about.',
          'se faire à quelque chose is "get used to it", and the y stands in for à l\'administration — hence je m\'y fais, je ne m\'y ferai jamais. The vous in dont personne ne vous avait parlé is the impersonal one: anybody at all, not the person you\'re talking to.',
        ],
        [
          'Ce n\'est pas la nourriture qui me manque, c\'est de pouvoir plaisanter avec mes amis sans réfléchir à chaque mot.',
          'It\'s not the food I miss, it\'s being able to joke around with friends without weighing every word.',
          'manquer runs backwards from English: the thing you miss is the subject and you are the object — mes amis me manquent, never "je manque mes amis."',
        ],
        [
          'Franchement, je ne crois pas qu\'un pays soit meilleur que l\'autre ; on gagne d\'un côté ce qu\'on perd de l\'autre, voilà tout.',
          'Honestly, I don\'t think one country is better than the other — you gain on one side what you lose on the other, that\'s all.',
          'je ne crois pas que pulls the subjunctive: soit, not est. Put it in the affirmative — je crois qu\'un pays est meilleur — and the indicative comes straight back.',
        ],
        [
          'Cela dit, si c\'était à refaire, je repartirais sans hésiter : on ne comprend vraiment son propre pays qu\'une fois qu\'on l\'a quitté.',
          'That said, if I had it to do over I\'d go again without hesitating — you never really understand your own country until you\'ve left it.',
        ],
        ],
      },
      {
        slug: 'histoire',
        title: 'A small disaster',
        nativeTitle: 'Une petite catastrophe',
        useWhen:
          'When the conversation turns to how your week went and you want to tell the story of the thing that went wrong, from the setup to the lesson learned.',
        tags: ['storytelling', 'past tense', 'small talk'],
        level: 4,
        blocks: [
        [
          'Il m\'est arrivé un truc bête il y a quinze jours : je descendais juste sortir les poubelles, en chaussons, sans veste ni téléphone.',
          'Something dumb happened to me two weeks ago — I just went down to take out the trash, in slippers, no jacket, no phone.',
        ],
        [
          'La porte a claqué derrière moi et j\'ai compris tout de suite que mes clés étaient restées sur la table de la cuisine.',
          'The door slammed behind me and I knew right away my keys were sitting on the kitchen table.',
          'Claquer goes both ways, and which one you pick decides whose fault this is: la porte a claqué = it swung shut on its own, j\'ai claqué la porte = you slammed it. Avoid the second here for a further reason — claquer la porte is the standard idiom for storming out or quitting on the spot, so it tells a completely different story.',
        ],
        [
          'J\'ai paniqué cinq bonnes minutes et j\'ai sonné chez tous les voisins en me répétant qu\'il fallait que je trouve une solution.',
          'I panicked for a good five minutes and rang every neighbor\'s bell, telling myself over and over that I had to come up with something.',
          'Bon goes between the number and the noun — cinq bonnes minutes, une bonne heure, deux bons kilomètres — and it means the count is generous: five minutes at least, probably more. Il fallait que forces the subjunctive, and in speech you keep the present one (que je trouve) even inside a past story — que je trouvasse is literary and sounds absurd out loud.',
        ],
        [
          'Finalement, c\'est la gardienne qui m\'a ouvert avec son double, sinon j\'en aurais eu pour deux cents euros de serrurier.',
          'In the end the building manager let me in with her spare key — otherwise a locksmith would have run me two hundred euros.',
          'En avoir pour is how you price something in conversation: j\'en ai eu pour trente euros, on en a pour deux heures. Coûter would be correct but flatter, and it can\'t do the time sense.',
        ],
        [
          'Avec le recul, ça me fait rire, mais sur le moment je me suis senti complètement idiot, planté sur le palier en chaussons.',
          'Looking back it\'s funny, but at the time I felt like a complete idiot, standing there on the landing in my slippers.',
          'Avec le recul / sur le moment is the pair French uses to split now from then; keep both when you tell the story. A woman says je me suis sentie complètement idiote, plantée — the participles and the adjective all agree with you.',
        ],
        [
          'Depuis, je ne sors plus jamais sans mon téléphone, et j\'ai laissé un double chez ma voisine du dessus, au cas où.',
          'Ever since, I never go out without my phone, and I\'ve left a spare key with my upstairs neighbor, just in case.',
          'Au cas où can end the sentence on its own, exactly as it does here. Spelled out it takes the conditional — au cas où ça se reproduirait — never the subjunctive, which is the mistake almost everyone makes.',
        ],
        ],
      },
      {
        slug: 'pression',
        title: 'Work pressure',
        nativeTitle: 'Le boulot et le reste',
        useWhen:
          'When someone asks how work is going and you want to say honestly that you\'re overloaded — without it turning into a complaint — and then hand the question back to them.',
        tags: ['work', 'stress', 'small talk'],
        level: 5,
        blocks: [
        [
          'Là, pour être honnête, je suis un peu sous l\'eau, mais bon, ça va se tasser.',
          'Admit you\'re swamped right now — and immediately take the edge off it.',
          'Là at the head of a sentence means "right now, as things stand", not "there". Dropping it makes the line a general confession instead of a report on this week.',
        ],
        [
          'Ce n\'est pas tant la charge de travail que le fait de ne jamais vraiment décrocher.',
          'Pin down what actually wears you out, as opposed to what people assume.',
          '« pas tant X que Y » corrects someone\'s assumption without flatly contradicting it. décrocher here is switching off mentally — nothing to do with picking up or hanging up a phone.',
        ],
        [
          'Le soir, je me surprends à rouvrir ma boîte mail à onze heures, alors que rien n\'est urgent.',
          'Own up to the habit that gives you away — the inbox at eleven at night.',
        ],
        [
          'Du coup, je me suis fixé une règle : après vingt heures, je ne réponds plus. Enfin, j\'essaie.',
          'State the boundary you\'ve set yourself, then admit how well it\'s holding.',
          'French puts a space before : ; ! and ? — « une règle : après ». Omitting it is one of the most visible tells that a sentence wasn\'t typed by a native.',
        ],
        [
          'Je ne vais pas me plaindre, j\'aime ce que je fais, n\'empêche que je ne tiendrais pas ce rythme pendant des années.',
          'Head off the sympathy, then say how unsustainable it really is.',
          'n\'empêche que is the pivot for taking back what you just conceded. Unlike bien que it takes the indicative, never the subjunctive. The conditional tiendrais makes it a verdict rather than a prediction.',
        ],
        [
          'Et vous, vous arrivez à faire la coupure, ou c\'est pareil de votre côté ?',
          'Hand it back: do they manage to keep the two apart, or is it the same where they are?',
          'faire la coupure is the everyday idiom for breaking cleanly between work and home — « je n\'arrive pas à faire la coupure » is what a French colleague actually says; cloisonner is the same idea in a more corporate register. Not faire la part des choses, which means judging with nuance or making allowances, not separating the two. The whole island is on vous; with someone you\'re on tu terms with this becomes « Et toi, tu arrives à faire la coupure… » and nothing else changes.',
        ],
        ],
      },
      {
        slug: 'technologie',
        title: 'AI and your work',
        nativeTitle: 'L\'IA et mon métier',
        useWhen:
          'When someone asks whether AI is coming for your job and you want to give a real answer — what you happily hand over, what you don\'t, and why — instead of a shrug.',
        tags: ['work', 'technology', 'opinions'],
        level: 5,
        blocks: [
        [
          'Honnêtement, je ne crois pas que l\'IA puisse remplacer mon métier ; elle le déplace. Nuance.',
          'Honestly, I don\'t think AI is going to replace my job — it\'s moving it, and that\'s not the same thing.',
          'Negated *croire* pulls the subjunctive: *puisse*, not *peut*. And a bare « Nuance. » at the end is a fixed move — it means "the distinction is the whole point". It lands harder as its own sentence than tacked on with a comma.',
        ],
        [
          'Tout ce qui est répétitif — les premiers jets, la mise en forme, la paperasse — tant mieux si une machine s\'en charge.',
          'Anything repetitive — first drafts, formatting, paperwork — fine by me if a machine takes it off my hands.',
        ],
        [
          'En revanche, tout ce qui relève du jugement — savoir quelle question poser, à qui, et quand se taire — je ne vois pas bien comment on l\'automatiserait.',
          'The judgment side, though — knowing what to ask, who to ask, when to keep quiet — I don\'t really see how you\'d automate that.',
          'The hedge is the conditional *automatiserait*: automating it is treated as a hypothetical operation — how you\'d go about it — rather than something denied outright. The categorical version is *je ne vois pas comment on peut l\'automatiser*. Note the shape too: the long topic is announced up front and only picked up at the very end by the bare *l\'*, which is what lets the sentence carry that much material before the verb.',
        ],
        [
          'Et puis il y a la question de la responsabilité : le jour où ça dérape, ce n\'est pas l\'outil qui rend des comptes.',
          'And then there\'s accountability: when it all goes sideways, the tool isn\'t the one who answers for it.',
          '*Le jour où* takes the plain present for something still hypothetical — *le jour où ça dérape*, not the subjunctive and not, in speech, the future.',
        ],
        [
          'Moi, je m\'en sers surtout comme d\'un stagiaire doué mais un peu trop sûr de lui : ça déblaie le terrain, et c\'est moi qui relis.',
          'I mostly use it like a bright intern who\'s a bit too sure of himself: it clears the ground, and I\'m the one who checks the work.',
          '*Se servir de X comme d\'un Y* keeps the second *de* — without it, *comme un stagiaire* would mean you use it the way an intern would. And after *c\'est moi qui*, the verb agrees with *moi*: **relis**, never *relit*.',
        ],
        [
          'Après, je me méfie un peu de mes propres certitudes : il y a vingt ans, je disais exactement la même chose de la traduction automatique.',
          'Then again, I\'m wary of my own certainties: twenty years ago I said exactly the same thing about machine translation.',
          '*Après,* at the head of a sentence has nothing to do with time — in speech it means "then again" / "that said" (*cela dit* is the neutral written equivalent). It walks a strong claim back a step without giving it up.',
        ],
        ],
      },
      {
        slug: 'reunion',
        title: 'Running a meeting',
        nativeTitle: 'Mener une réunion',
        useWhen:
          'When you are the one running a work meeting in French — opening it, putting a proposal on the table, saying a date will not work without sounding difficult, and closing it out.',
        tags: ['work', 'meetings', 'formal'],
        level: 5,
        blocks: [
        [
          'Merci à tous d\'avoir pu vous libérer. Si vous le voulez bien, on va commencer par le point le plus urgent.',
          'Thanks for making the time, everyone. If nobody objects, let\'s start with the item that can\'t wait.',
          'The island is vous throughout, but the room\'s "we" is on, not nous: nous commencerons reads like written minutes, not like someone speaking. Si vous le voulez bien is the hinge that opens a meeting — it asks permission and takes it in the same breath.',
        ],
        [
          'Je vous propose qu\'on parte sur une version allégée pour le premier jalon, quitte à l\'enrichir par la suite.',
          'What I\'d suggest is we go with a stripped-down version for the first milestone, and build it out later.',
          'Partir sur is the ordinary business verb for going with an option — on part sur cette solution, sur ce chiffre. Quitte à + infinitive concedes the cost of your own proposal before anyone else can raise it, which is what keeps it a suggestion rather than a push.',
        ],
        [
          'Sur les délais, en revanche, je serai franc avec vous : le 15 me paraît un peu juste.',
          'On the timeline, though, I\'ll be straight with you: the 15th is tight.',
          'Un peu juste is the standard way to call a date unworkable: it faults the calendar, not the person who set it. C\'est impossible or on n\'y arrivera jamais would land as a challenge — here the understatement is doing the refusing.',
        ],
        [
          'Rien d\'insurmontable, mais si on veut tenir la date, il faudra faire des arbitrages sur le périmètre.',
          'Nothing insurmountable, but if we\'re holding that date, something has to give on scope.',
          'Arbitrer here is not legal arbitration: in corporate French it is deciding what gets dropped when not everything fits, and faire des arbitrages sur + the area is the fixed phrase for cuts landing there. Blunter and equally native: il faudra rogner sur le périmètre.',
        ],
        [
          'J\'entends bien la contrainte de votre côté. Est-ce qu\'on peut au moins se garder une semaine de battement avant la livraison ?',
          'I take the point about the pressure on your side. Could we at least keep a week of slack before delivery?',
          'J\'entends (never j\'écoute) means the point has been taken on board — it is the French "I hear you", and it carries the unspoken "but" so you don\'t have to say it. Battement is slack in a schedule.',
        ],
        [
          'Très bien, on va s\'arrêter là. Je vous envoie un compte rendu d\'ici demain et je reviens vers vous dès que j\'aurai du nouveau.',
          'Right, let\'s leave it there. I\'ll send out the notes by tomorrow and come back to you as soon as I know more.',
          'Revenir vers vous is fixed for "get back to you"; retourner à vous is an English calque and means nothing. And dès que takes the future in French — dès que j\'aurai, not dès que j\'ai.',
        ],
        ],
      },
      {
        slug: 'conviction',
        title: 'Holding your ground',
        nativeTitle: 'Concéder sans céder',
        useWhen:
          'When a discussion with a colleague, a host, or someone at the table turns to something contested and you want to defend what you actually think without either caving or picking a fight.',
        tags: ['debate', 'concession', 'hedging'],
        level: 6,
        blocks: [
        [
          'Quitte à me mettre tout le monde à dos, je maintiens que, sur ce point précis, on se trompe de combat.',
          'Open with your claim while flagging up front that you know it won\'t be popular.',
        ],
        [
          'Autant je vous rejoins sur le constat, autant sur les solutions, je ne vous suis plus.',
          'Hand them the part you genuinely agree with, then stop dead at the part you don\'t.',
          'autant… autant… needs both halves — drop the second and the sentence loses its spine. The island stays on vous throughout: the register a real disagreement with someone you aren\'t close to takes.',
        ],
        [
          'Vos chiffres, je vous les accorde volontiers ; il n\'en reste pas moins qu\'ils ne disent rien de ce qui se passe sur le terrain.',
          'Concede the evidence outright, then say why it still doesn\'t settle the question.',
        ],
        [
          'Ne me faites pas dire ce que je n\'ai pas dit : je ne défends pas le système actuel, je dis simplement qu\'on ne m\'a rien proposé de mieux.',
          'Push back when someone restates your position as something worse than it is.',
        ],
        [
          'Encore faut-il s\'entendre sur ce qu\'on met derrière le mot « liberté », sans quoi on va discuter toute la soirée sans parler de la même chose.',
          'Stop the argument to pin down a word the two of you are plainly using differently.',
          'Encore in this sense forces inversion: encore faut-il, never encore il faut. And guillemets take a space on the inside — « liberté », not «liberté».',
        ],
        [
          'Je peux très bien me tromper, et je ne demande qu\'à être convaincu, mais tant qu\'on ne m\'aura pas prouvé le contraire, je persiste et signe.',
          'Close without backing down and without turning it into a fight.',
          'tant que takes the futur antérieur where English keeps the present ("until someone proves otherwise"), and the ne is obligatory: tant qu\'on ne m\'aura pas prouvé. Je persiste et signe is a set phrase — courtroom-flavored, and used with a half-smile.',
        ],
        ],
      },
      {
        slug: 'expressions',
        title: 'Idioms that land',
        nativeTitle: 'Les expressions qui font mouche',
        useWhen:
          'When something at work has stalled and you want to talk about it the way a French colleague would — resigned, a little wry — instead of in correct but transparently foreign French.',
        tags: ['idioms', 'register', 'work'],
        level: 6,
        blocks: [
        [
          'Disons qu\'on n\'est pas sorti de l\'auberge.',
          'Let\'s just say we\'re nowhere near out of the woods.',
          'Sets the register for the island: spoken, but nothing you\'d regret saying at work. The ne is kept here, which is safe in any setting; in relaxed speech it drops (on est pas sorti de l\'auberge), and natives drop it unevenly from one sentence to the next — don\'t turn keeping or dropping it into a policy you apply across all six lines.',
        ],
        [
          'C\'est la croix et la banniere pour leur arracher une decision.',
          'Getting a decision out of them is like pulling teeth.',
          'When you spell out what was so hard, it comes in as pour + infinitive, not as a direct complement; the expression also stands on its own — le demenagement, ca a ete la croix et la banniere. And it\'s arracher, not obtenir, that carries the exasperation.',
        ],
        [
          'Cela dit, je ne vais pas en faire une maladie.',
          'That said, I\'m not going to lose any sleep over it.',
          'The en is obligatory and points back at the annoyance. Keep it for your own reaction: n\'en fais pas une maladie, aimed at someone else, tells them their grievance is childish, which starts a fight rather than ending one.',
        ],
        [
          'De toute facon, je n\'en suis pas a une semaine pres.',
          'Anyway, one more week isn\'t going to kill me.',
          'ne pas en etre a X pres = one more X changes nothing at this point. Neutral about time, brutal about people: il n\'en est pas a un mensonge pres says the man lies constantly.',
        ],
        [
          'Je vais les relancer une derniere fois, histoire de pouvoir dire que j\'aurai tout essaye.',
          'I\'ll chase them one more time, if only so I can say I tried everything.',
          'Histoire de is the familiar register — at home in speech and in an email, out of place in formal prose, where it\'s pour or afin de; the meaning is the same either way. J\'aurai tout essaye is the ready-made phrase (tout tente sounds written), and the futur anterieur stands you at the end of it all looking back, which is the gesture the line is making. Relancer is the office word for chasing someone up; rappeler isn\'t.',
        ],
        [
          'Ce n\'est pas demain la veille qu\'ils vont trancher.',
          'They\'re not going to come down on one side any time soon.',
          'Pas de sitot is the neutral version of this; pas demain la veille is resigned, with a jab in it. Trancher is finally picking a side — the very thing they\'re dodging — so decider would flatten the line.',
        ],
        ],
      },
  ],
  es: [
      {
        slug: 'presentarse',
        title: 'Introducing yourself',
        nativeTitle: 'Mucho gusto',
        useWhen:
          'The first thirty seconds with anyone new — a colleague, a host, someone at the next table — when you need to give your name, get theirs, say why you\'re here, and get out cleanly.',
        tags: ['introductions', 'greetings', 'politeness'],
        level: 1,
        blocks: [
        [
          'Buenos días. Me llamo Daniel.',
          'Good morning. My name\'s Daniel.',
          'Me llamo is literally "I call myself" — it\'s what people actually say. Mi nombre es isn\'t wrong, but it sounds like reading off a form.',
        ],
        [
          'Y usted, ¿cómo se llama?',
          'And you — what\'s your name?',
          'se llama, not te llamas — that\'s usted, the polite you. It\'s one of only two lines in the island with a "you" in it (the other is ¿Puede hablar más despacio?); everything else you say here is first person and works with anyone. If the other person tutea you, this line alone becomes ¿Y tú, cómo te llamas?',
        ],
        [
          'Mucho gusto. Soy de Chicago, en Estados Unidos.',
          'Nice to meet you. I\'m from Chicago, in the States.',
        ],
        [
          'Estoy aquí por trabajo. Soy ingeniero.',
          'I\'m here for work. I\'m an engineer.',
          'No article before the job: soy ingeniero, never soy un ingeniero. A woman says soy ingeniera.',
        ],
        [
          'Hablo un poco de español. ¿Puede hablar más despacio, por favor?',
          'I speak a little Spanish. Could you slow down a bit?',
          'Puede keeps the usted of ¿cómo se llama? — puedes if you\'ve switched to tú. Más despacio is what people actually say; más lentamente is bookish and marks you as reading off a phrasebook.',
        ],
        [
          'Bueno, un placer. Hasta luego.',
          'Well, it\'s been a pleasure. See you later.',
          'Bueno here isn\'t "good" — it\'s the signal that you\'re wrapping up, like "right, then." It\'s what makes the exit read as polite rather than abrupt.',
        ],
        ],
      },
      {
        slug: 'barra',
        title: 'Ordering a drink',
        nativeTitle: 'Pedir en la barra',
        useWhen:
          'When you\'re at a café or bar counter and have to order, set the size, say whether it\'s to go, order for someone with you, pay, and chase the drink when it doesn\'t come.',
        tags: ['coffee', 'ordering', 'paying'],
        level: 1,
        blocks: [
        [
          'Un café con leche, por favor.',
          'A coffee with milk, please.',
          'The bare noun plus «por favor» is a complete order — no verb needed. If you want one, the natural softeners are the imperfect «Quería un café con leche» or, in Spain, «Me pone un café con leche». «Quiero un café» is said everywhere and is not rude; it just states a want instead of asking, so it lands flat.',
        ],
        [
          '¿Me lo pone grande, por favor?',
          'Can you make that a large?',
          '«Me pone…» is how you ask to be served in a Spanish bar; «¿me da…?» does the same job and travels better in Latin America. Both are plain present tense — you never need «quisiera». Size rides on the drink, never on an article: «un café con leche grande» at order time, «¿me lo pone grande?» or bare «grande, por favor» when you add it after. «El grande» sends them looking for the big croissant. This island stays in usted throughout; with tú it is «¿me lo pones…?» and «perdona».',
        ],
        [
          'Para llevar, por favor. Tengo prisa.',
          'To go, please. I\'m in a hurry.',
          'They usually ask first: «¿Para tomar aquí o para llevar?» Answer with just the half you want — «Para llevar» or «Para aquí». Nothing else goes with it.',
        ],
        ['Y para mi amigo, un té, por favor.', 'And a tea for my friend.'],
        [
          '¿Cuánto es todo? ¿Puedo pagar con tarjeta?',
          'How much is that all together? Can I pay by card?',
          '«¿Cuánto es?» asks for the total sitting in front of you. «¿Cuánto cuesta?» asks the price of one thing, which is the wrong question once they have rung it all up.',
        ],
        [
          'Perdone, ¿y mi café con leche?',
          'Excuse me — what about my coffee?',
          '«Perdone» is for getting someone\'s attention; «lo siento» apologizes for something you did, so it is the wrong word here. The bare «¿y mi café?» is the entire complaint, and it lands as mild rather than rude.',
        ],
        ],
      },
      {
        slug: 'oficio',
        title: 'What I do at work',
        nativeTitle: 'A qué me dedico',
        useWhen:
          'When someone asks what you do and "I work in data" won\'t survive the next three questions — a dinner, a conference, meeting your partner\'s colleagues.',
        tags: ['work', 'introductions', 'small talk'],
        level: 4,
        blocks: [
        [
          'Básicamente me encargo de que los datos con los que trabajan los demás equipos sean fiables; suena aburrido, pero si fallan, se para todo.',
          'Basically I make sure the data the other teams work with is reliable — it sounds dull, but if it\'s wrong, everything stops.',
          'Encargarse de que forces the subjunctive (sean, not son): you\'re making something be the case, not reporting that it is. This is the opener the rest of the island hangs off, so drill the sean.',
        ],
        [
          'Un día normal empieza con media hora de correos y una reunión rápida de equipo; a partir de ahí depende de lo que se haya roto.',
          'A normal day starts with half an hour of email and a quick team meeting; after that it depends on whatever\'s broken.',
        ],
        [
          'Trato con gente que no es técnica, así que se me va la mitad del día traduciendo lo que piden a algo que se pueda hacer.',
          'I deal with people who aren\'t technical, so half my day disappears into turning what they ask for into something we can actually build.',
          'Se me va la mitad del día — literally, half the day goes away on me. The dative me is the whole point: the time went, and not by your choosing. Me paso la mitad del día traduciendo is just as native, but neutral — it reports how the time was spent, with no complaint in it.',
        ],
        [
          'La verdad es que llegué a esto por casualidad: entré para un proyecto de seis meses y acabé llevando el área entera.',
          'Honestly, I fell into this: I came in for a six-month project and ended up running the whole department.',
        ],
        [
          'Lo que más me gusta es que nadie me dice cómo hacerlo; lo que menos, que cuando algo sale mal, el primero al que llaman soy yo.',
          'What I like most is that nobody tells me how to do it; what I like least is that when something goes wrong, I\'m the first one they call.',
        ],
        [
          'Si te interesa, te pongo un ejemplo concreto, porque así, en abstracto, no se entiende muy bien a qué me dedico.',
          'If you\'re interested, I\'ll give you a concrete example — in the abstract like this, what I do doesn\'t really land.',
          'Poner un ejemplo is the default collocation: te pongo un ejemplo. Dar un ejemplo is also correct and common in American Spanish; what you must not do is drop the article, because dar ejemplo means to set an example. This is also the only line where register shows — tú, for a colleague or someone you\'ve been introduced to. With a client or an interviewer, end the island si le interesa, le pongo.',
        ],
        ],
      },
      {
        slug: 'comparacion',
        title: 'Here versus back home',
        nativeTitle: 'Aquí y allí',
        useWhen:
          'When someone who knows you moved here asks how the two countries compare, and you want to answer with real detail — what shocked you, what you\'ve absorbed, what still defeats you — without ranking one above the other.',
        tags: ['expat', 'culture shock', 'comparing'],
        level: 4,
        blocks: [
        [
          'Lo que más me chocó al principio fue el horario: aquí nadie cena antes de las nueve y media, y yo me moría de hambre.',
          'What threw me most at first was the schedule — nobody here eats dinner before nine thirty, and I was starving.',
          'chocar here is neither "crash" nor "shock": me chocó is "it struck me as odd," and in Spain it\'s what a native reaches for long before sorprender. In Mexico and much of Latin America me choca means "I can\'t stand it" — there, use me sorprendió or me llamó la atención.',
        ],
        [
          'Al final me he acostumbrado a casi todo, incluso a que la gente hable a gritos en la mesa; ya ni me doy cuenta.',
          'By now I\'ve gotten used to almost everything, even people shouting across the dinner table — I don\'t even notice anymore.',
          'acostumbrarse a que forces the subjunctive: a que la gente hable, never habla. With a plain noun there\'s no que and no subjunctive — me he acostumbrado al ruido.',
        ],
        [
          'Lo que todavía se me atraganta es el papeleo: cada vez que renuevo algo, salgo de la oficina con más dudas que cuando entré.',
          'The thing that still gets me is the paperwork — every time I renew something I leave the office with more questions than I came in with.',
          'atragantarse works like gustar — the thing is the subject, you\'re the dative. Se me atraganta el papeleo, but se me atragantan los impuestos; never yo atraganto. Literally it\'s food going down the wrong way, so figuratively it\'s the one thing you still can\'t swallow.',
        ],
        [
          'Nunca pensé que fuera a echar de menos algo tan tonto como el pan del barrio donde crecí, pero ya ves.',
          'I never thought I\'d miss something as silly as the bread from the neighborhood I grew up in, but there you go.',
        ],
        [
          'No me hagas elegir: no es que una vida sea mejor que la otra, es que son cosas distintas y ya está.',
          'Don\'t make me pick — it\'s not that one life is better than the other, they\'re just different, end of story.',
          'no es que + subjunctive (sea) sets up what you\'re rejecting; the es que that answers it goes back to the indicative (son). The island is first person throughout except two tú forms — no me hagas here and pero ya ves in the line above. On usted they become no me haga elegir and pero ya ve: change both or neither.',
        ],
        [
          'Me quedo con lo mejor de cada sitio, y supongo que por eso ya no me siento del todo ni de aquí ni de allí.',
          'I take the best of both places, and I guess that\'s why I don\'t fully belong to either one anymore.',
        ],
        ],
      },
      {
        slug: 'anecdota',
        title: 'A small disaster',
        nativeTitle: 'Un pequeño desastre',
        useWhen:
          'When someone asks how your trip went or why you look wrecked, and you want to tell the whole story of something that went wrong — the setup, the moment it broke, the panic, how it got fixed, and what you do differently now.',
        tags: ['storytelling', 'past narration', 'mishaps'],
        level: 4,
        blocks: [
        [
          'No te vas a creer lo que me pasó: me fui de viaje una semana y dejé la lavadora puesta antes de salir corriendo.',
          'You won\'t believe what happened to me — I went away for a week and left the washing machine running as I ran out the door.',
          'The whole island is tú — you are telling this to a friend, or to someone you are easy with. For a stranger or a client it opens No se va a creer lo que me pasó, and the only other line that has to move is the te juro in block 3, which becomes le juro. Poner la lavadora is how you say you ran a load; dejarla puesta is leaving it running while you are out.',
        ],
        [
          'Resulta que se soltó la manguera a mitad del ciclo y el agua terminó filtrándose al apartamento de abajo.',
          'Turns out the hose came loose halfway through the cycle and the water ended up leaking into the apartment downstairs.',
          'Resulta que is the hinge of any spoken story — it hands over the twist without you having to announce that you are getting to the point.',
        ],
        [
          'Me enteré por un mensaje de la vecina de abajo y te juro que se me heló la sangre porque yo estaba a mil kilómetros.',
          'I found out by text from my downstairs neighbor, and my blood ran cold, because I was a thousand miles away.',
          'Enterarse por is the channel the news reached you through — por un mensaje, por mi hermano; what you found out takes de instead: me enteré de lo del agua. The yo is optional and doing real work — it is there only to set you against the water, a thousand kilometers apart.',
        ],
        [
          'Por suerte, mi hermano tiene una copia de las llaves y pudo cortar el agua antes de que se estropeara el suelo.',
          'Luckily my brother has a spare key and managed to shut the water off before the floor was ruined.',
          'Antes de que takes the subjunctive every single time, with no exception to look for — and a past main verb pulls it into the imperfect subjunctive: estropeara.',
        ],
        [
          'Al final el seguro cubrió casi todo, aunque la pintura del techo de la vecina la tuve que pagar de mi bolsillo.',
          'In the end the insurance covered nearly all of it — the one thing I had to pay for out of my own pocket was repainting the neighbor\'s ceiling.',
          'When you front the object for emphasis — la pintura first — Spanish makes you repeat it as a pronoun before the verb: la tuve que pagar. Dropping that la is one of the clearest tells that a sentence was built in English first.',
        ],
        [
          'Desde entonces no pongo la lavadora si no voy a estar en casa, y cierro el agua siempre que salgo de viaje.',
          'Now I never run the washing machine unless I\'m going to be home, and I shut the water off whenever I go away.',
        ],
        ],
      },
      {
        slug: 'presion',
        title: 'Work pressure',
        nativeTitle: 'El trabajo y todo lo demás',
        useWhen:
          'When a colleague or friend asks how work is going and you want to say something true about the strain and where you draw the line, instead of "estoy muy ocupado".',
        tags: ['work', 'burnout', 'hedging'],
        level: 5,
        blocks: [
        [
          'La verdad es que llevo un par de meses con el agua al cuello, pero bueno, no me puedo quejar.',
          'Honestly, I\'ve been up to my neck for a couple of months now — but I can\'t complain.',
        ],
        [
          'Lo que me desgasta no son las horas en sí, sino no poder desconectar del todo.',
          'It\'s not the hours as such that wear me down, it\'s never quite being able to switch off.',
        ],
        [
          'Por eso me he puesto una norma: a partir de las siete, el correo ni lo abro.',
          'So I\'ve made myself a rule: after seven, I don\'t even open my email.',
          'Fronting the object and picking it up again with a clitic — el correo ni lo abro — is how spoken Spanish puts weight on it. No abro el correo is correct and says half as much. (Me he puesto is peninsular; in most of Latin America, me puse.)',
        ],
        [
          'Otra cosa es que la cumpla, claro.',
          'Whether I stick to it is another matter, obviously.',
          'Otra cosa es que takes the subjunctive — cumpla, never cumplo — and is the standard dry way to concede that what you\'ve just claimed and what you actually do are two different things.',
        ],
        [
          'Pero tampoco voy a dejarme la salud en una empresa que mañana puede prescindir de mí.',
          'But I\'m not about to wreck my health for a company that could drop me tomorrow.',
          'Pero tampoco here is not the \'neither\' of a second negative — there is no earlier negative clause for it to echo. It caps the concession you just made: \'that said, I\'m not going as far as…\'. It\'s the everyday way of drawing a line after an admission.',
        ],
        [
          '¿Y en tu caso? ¿Te compensa o ya estás pensando en cambiar de aires?',
          'What about you — is it worth it, or are you already looking for a change of scene?',
          'Compensar isn\'t \'compensate\' here: ¿te compensa? asks whether the trade is worth it, and it\'s the everyday verb for that calculation. The island is tú throughout — a colleague or a friend; with someone you\'d usted, it\'s ¿y en su caso?, ¿le compensa?',
        ],
        ],
      },
      {
        slug: 'tecnologia',
        title: 'AI and my work',
        nativeTitle: 'La IA en lo mío',
        useWhen:
          'When talk turns to whether AI is coming for your job — at a work dinner, in the corridor at a conference, with a colleague who wants your honest read rather than a headline.',
        tags: ['work', 'ai', 'opinions'],
        level: 5,
        blocks: [
        [
          'En lo mío, la verdad es que la IA no me quita el sueño, pero tampoco me lo tomo a la ligera.',
          'In my line of work, honestly, AI doesn\'t keep me up at night — but I don\'t take it lightly either.',
        ],
        [
          'Por ahora, lo que sí me está quitando es la parte más mecánica del trabajo, y mira, que se la quede.',
          'So far what it\'s actually taking off my plate is the most mechanical part of the job — and frankly, it\'s welcome to it.',
          '«Que se la quede» is a bare que + subjunctive with no main verb behind it — Spanish\'s way of saying let it have it. The la is la parte. «Mira» is the tú form; with someone you address as usted it would be «mire», and the rest of the island stays first person either way.',
        ],
        [
          'Lo que no veo que pueda sustituir es el criterio: saber cuál es de verdad el problema antes de ponerse a resolverlo.',
          'What I don\'t see it replacing is judgment — working out what the problem actually is before you set about solving it.',
        ],
        [
          'Y ojo, no soy de los que piensan que esto se va a quedar en nada.',
          'And mind you, I\'m not one of those people who thinks this is all going to come to nothing.',
          'After de los que the verb agrees with los que, so piensan, not pienso; «soy de los que pienso» is heard but reads as careless. «Ojo» here is mind you — it heads off the assumption that you\'re about to be a skeptic.',
        ],
        [
          'Lo que me preocupa no es tanto que me sustituya, sino que se acabe normalizando un trabajo mediocre porque sale más barato.',
          'What worries me isn\'t so much being replaced as mediocre work becoming the norm because it comes out cheaper.',
          'Both halves hang off «lo que me preocupa es…», so both are que-clauses and both take the subjunctive — sustituya, acabe. The que after sino is the clause\'s own, not a «sino que» conjunction: swap in the correlative como and it survives — «no es tanto que me sustituya como que se acabe normalizando». «Acabar + gerundio» is end up -ing; it frames the outcome as drift, something nobody decided.',
        ],
        [
          'Al final va a pasar lo de siempre: no me sustituye la máquina, me sustituye quien sepa sacarle partido.',
          'In the end it\'ll go the way it always does: the machine isn\'t what replaces me — whoever knows how to get the most out of it is.',
        ],
        ],
      },
      {
        slug: 'reunion',
        title: 'Business meetings',
        nativeTitle: 'Reuniones de trabajo',
        useWhen:
          'When you have to chair a meeting in Spanish with clients or people you don\'t tutear — open it, put your proposal on the table, resist a date without souring the room, and close.',
        tags: ['business', 'formal', 'negotiation'],
        level: 5,
        blocks: [
        [
          'Si les parece, empezamos. Tenemos una hora y me gustaría que saliéramos de aquí con una fecha cerrada.',
          'Starting the meeting and saying up front what you want out of it.',
          'Formal ustedes/les, held in every line — the register for clients, or for anyone you wouldn\'t tutear. The form to watch is not tú (no line here addresses one person) but vosotros: with your own team in Spain this island runs «si os parece» and «os hago llegar», and ustedes to your own colleagues there sounds cold. Across Latin America ustedes is the only plural and carries no formality either way, so the lines stand as written. «Si les parece» proposes rather than announces; without it, «empezamos» is a chairman giving an order.',
        ],
        [
          'Nuestra propuesta pasa por adelantar las pruebas a septiembre y dejar la formación para el final.',
          'Putting your proposal on the table.',
          '«Pasar por + infinitivo» presents the proposal as the route the plan has to take — the same construction as «la solución pasa por…». «Nuestra propuesta es adelantar…» is equally native and equally correct; the «pasa por» version just sounds like someone who chairs meetings for a living. «Formación» is Spain — in Latin America workplace training is «capacitación».',
        ],
        [
          'Estoy de acuerdo en lo esencial; ahora bien, hay un punto que sí matizaría.',
          'Conceding the whole so you can disagree with one part of it.',
          '«Matizar» — to qualify — is the polite verb for pushing back; «no estoy de acuerdo» would be a much bigger move than you intend. «Ahora bien» is the formal register\'s contrastive: it grants what was just said in full and then opens the qualification, of a piece with «matizar» and «les hago llegar». «Pero» here is not rude, just neutral — it drops the line a notch, nothing more.',
        ],
        [
          'Siendo realistas, con ese calendario se nos queda todo muy justo.',
          'Saying the deadline is too tight, without blaming whoever set it.',
          '«Nos» is the dative of interest — the tightness happens to the team, nobody causes it; the «se» is pronominal «quedarse», the same shape as «se nos hizo tarde». Drop it and «nos queda todo muy justo» is just as blameless. The diplomacy is really lexical: «siendo realistas» makes the objection sober arithmetic rather than complaint, and «ese calendario» keeps whoever drew it up out of the sentence.',
        ],
        [
          'Podemos comprometernos a esa fecha, siempre y cuando cerremos los requisitos esta semana.',
          'Accepting the deadline, but attaching your condition to it.',
        ],
        [
          'Si no hay nada más, lo dejamos aquí. Les hago llegar un resumen esta misma tarde.',
          'Closing the meeting and saying what happens next.',
          '«Hacer llegar» is the register the rest of the island is in; «les mando» is fine between colleagues but drops a notch and you can hear it.',
        ],
        ],
      },
      {
        slug: 'postura',
        title: 'Holding your ground',
        nativeTitle: 'Ceder sin dar el brazo a torcer',
        useWhen:
          'When you are arguing something you actually believe with someone whose position you partly respect, and you want to concede everything that is true without giving up the point.',
        tags: ['debate', 'hedging', 'register'],
        level: 6,
        blocks: [
        [
          'Te concedo que el diagnóstico es acertado; donde ya no te sigo es en el remedio.',
          'Give them the whole first half of their argument, and disagree only with what they want to do about it.',
          'Tuteo throughout — a discussion between peers, not a formal debate. Peninsular throughout too: «me chirría» (3), «si me apuras» (4) and «igual» for "maybe" (6) are Spain, not Latin America. «Donde ya no te sigo» is literally "where I no longer follow you" — it marks the exact point you get off the train, and it lands far softer than «te equivocas» because the concession you just made is still standing behind it.',
        ],
        [
          'Una cosa es que yo cuestione el método y otra muy distinta que me pongas en la boca que hay que dar al traste con treinta años de trabajo.',
          'Refuse the strawman before it sets: mark the distance between what you actually said and the extreme version being put in your mouth.',
          'The subject changes across the two halves — «que yo cuestione» / «que me pongas» — and that forces subjunctive in both; with one subject throughout it collapses into two bare infinitives and loses its edge. «Dar al traste con» is to wreck something for good, and «poner en la boca» names the move you are objecting to: words you never said, reported as yours.',
        ],
        [
          'Sin ánimo de sentar cátedra, a mí lo que me chirría es que demos por sentado justo lo que habría que demostrar.',
          'Say what actually bothers you — that the argument assumes the very thing it is supposed to prove.',
          '«Sentar cátedra» is to pontificate, so «sin ánimo de sentar cátedra» is the standard educated disclaimer before a strong claim. «Chirriar» is to grate or squeak: it flags that something does not sit right without committing you to calling it false, and the reaction verb pulls the subjunctive after it — «es que demos», never «es que damos».',
        ],
        [
          'Y conste que el argumento contrario tiene su peso; si me apuras, en un escenario así yo también dudaría.',
          'Steelman their case: grant that it carries real weight and that, if pushed, you would hesitate yourself.',
        ],
        [
          'Ahora bien, en lo que no pienso transigir es en que se venda como un hecho consumado algo que todavía está por decidir.',
          'Name the one thing you will not give ground on: an open question being sold as a done deal.',
          '«Transigir» is to give ground to keep the peace, so refusing to is much stronger than «no estoy de acuerdo» without raising your voice. The cleft repeats the preposition on both sides — en lo que… es en que… — and dropping the second «en» is the classic slip here.',
        ],
        [
          'En fin, que igual el tiempo me quita la razón, pero de momento no he oído nada que me haga cambiar de opinión.',
          'Close graciously without moving an inch: allow that you could turn out to be wrong, and confirm that nothing said today has moved you.',
          '«Igual» here means "maybe", not "equally", and takes the indicative (igual me quita). After a negated antecedent the relative goes subjunctive — «nada que me haga», never «que me hace».',
        ],
        ],
      },
      {
        slug: 'expresiones',
        title: 'Idioms that land',
        nativeTitle: 'Frases hechas',
        useWhen:
          'When you\'re telling a colleague or a friend how a rough stretch at work actually went, and you want it to sound like something a Spaniard would say rather than something translated.',
        tags: ['idioms', 'work', 'register'],
        level: 6,
        blocks: [
        [
          'Estas semanas voy de culo con el trabajo: no doy abasto.',
          'I\'ve been completely swamped at work lately — I can\'t keep up.',
          'Peninsular. "Ir de culo" is crude-but-ordinary with colleagues you know, never with a client; in the Southern Cone "de culo" reads as lucky, not buried. Neutral swap: estoy hasta arriba de trabajo — keep the de-phrase, because bare estoy hasta arriba is how you say you\'re drunk.',
        ],
        [
          'El proyecto se nos fue de las manos y acabé sacándole las castañas del fuego a medio equipo.',
          'The project got away from us and I ended up bailing out half the team.',
          'The reproach lives in the dative. "Sacar las castañas del fuego" alone is just salvaging something; adding a alguien says they should have done it themselves — that\'s the whole complaint.',
        ],
        [
          'Lo dije en la reunión, eso sí, con la boca pequeña. Y claro, ni caso.',
          'I did raise it in the meeting — half-heartedly, mind you. And of course nobody paid any attention.',
          '"Con la boca pequeña" is an admission against yourself: you said it without conviction, so it didn\'t count. "Ni caso" is a bare noun phrase — no verb, and none is missing.',
        ],
        [
          'En el correo fui más diplomático: no quería poner en tela de juicio el criterio de nadie.',
          'In the email I was more diplomatic — I didn\'t want to call anyone\'s judgment into question.',
          'Deliberate register jump: "poner en tela de juicio" belongs to email and meetings. Over a beer the same thought is no quería llevarle la contraria a nadie.',
        ],
        [
          'Al final salvamos los muebles, pero por los pelos.',
          'In the end we salvaged what we could, but only just.',
        ],
        [
          'A partir de ahora hilo mucho más fino con los plazos y lo dejo todo por escrito, por si las moscas.',
          'From now on I\'m being far more exacting about deadlines, and putting everything in writing, just in case.',
          '"Hilar fino" is precision, not effort — not "work harder." "Por si las moscas" is spoken only; the same hedge in a written report is por precaución.',
        ],
        ],
      },
  ],
  it: [
      {
        slug: 'presentarsi',
        title: 'Introducing yourself',
        nativeTitle: 'Piacere, mi chiamo…',
        useWhen:
          'The first thirty seconds with anyone new — a hostel desk, a language class, the person on the next barstool — when you need to give your name, get theirs, say where you\'re from and why you\'re here, and get out politely.',
        tags: ['introductions', 'greetings', 'small talk'],
        level: 1,
        blocks: [
        ['Ciao, mi chiamo Daniel. Molto piacere!', 'Hi, I\'m Daniel. Good to meet you.'],
        [
          'E tu, come ti chiami?',
          'And you — what\'s your name?',
          'Everything in this island is «tu», the register for someone your own age in a bar, a hostel, a class. With an older stranger, or anyone working behind a desk, this line becomes «Come si chiama?» and «e tu» becomes «e Lei».',
        ],
        [
          'Sono americano, vengo da Seattle.',
          'I\'m American — I\'m from Seattle.',
          'No article after sono: sono americano, never «sono un americano». A woman says americana.',
        ],
        [
          'E tu, di dove sei? Sei di qui?',
          'And where are you from? Are you local?',
          'Which preposition you need depends on the verb, not on the question: essere takes di — «di dove sei?», «sono di Bologna» — while venire takes da, «da dove vieni?», «vengo da Seattle», exactly as in line 3. Both are everyday Italian. With essere, di comes before the question word, where English leaves "from" stranded at the end.',
        ],
        [
          'Sono qui per lavoro, solo per due settimane.',
          'I\'m here for work, just for two weeks.',
          'Per gives the reason for the trip — per lavoro, per studio — and then does double duty for how long you\'re staying: per due settimane. A holiday is the exception that normally takes in: «Sono qui in vacanza».',
        ],
        ['Scusa, adesso devo andare. A presto!', 'Sorry, I have to run. See you!'],
        ],
      },
      {
        slug: 'banco',
        title: 'Ordering a drink',
        nativeTitle: 'Al bar',
        useWhen:
          'You\'re standing at an Italian bar counter and want to order, add something for the person with you, pay for both, and say something when your coffee doesn\'t turn up.',
        tags: ['ordering', 'cafe', 'paying'],
        level: 1,
        blocks: [
        [
          'Buongiorno, prendo un caffè, per favore.',
          'Hi — I\'ll have a coffee, please.',
          'Italians order in the present tense with *prendo*, literally "I take". *Voglio un caffè* is grammatical but lands like a demand. And greet first: walking up and naming a drink cold sounds abrupt.',
        ],
        [
          'E una bottiglietta d\'acqua naturale, grazie.',
          'And a small bottle of still water, thanks.',
          'Say still or sparkling up front — *naturale* or *frizzante* — or you\'ll get *naturale o frizzante?* back, every time. *Bottiglietta* is the ordinary word for the small bottle; *una bottiglia piccola* is understood but sounds like you\'re describing the bottle rather than naming the thing.',
        ],
        [
          'Il caffè da portare via, per favore.',
          'The coffee\'s to go, please.',
          '*Da portare via* is fixed — it never changes shape, whatever you ordered. If you stay, you drink it standing at the counter, *al banco*; sitting down, *al tavolo*, is a separate and higher price list.',
        ],
        ['Anche un tè freddo per la mia amica.', 'And an iced tea for my friend too.'],
        [
          'Quant\'è? Pago tutto io.',
          'What do I owe? It\'s all on me.',
          '*Quant\'è* is the elided form you\'ll actually say; *quanto è* sounds written. Putting *io* after the verb is what turns *pago* into "I\'m the one paying" — Italian marks that with word order, not by stressing the word.',
        ],
        [
          'Scusi, aspetto ancora il caffè.',
          'Sorry — I\'m still waiting on my coffee.',
          'No possessive: *il mio caffè* is English showing through, and the article alone already means the one you ordered. *Aspettare* takes no preposition — you wait something, not "for" it. *Scusi* is the lei form, the polite you, for anyone behind a counter (*scusa* is for friends); it\'s the only word in this island marked for formality, so it\'s the one that sets the register for whatever you say next.',
        ],
        ],
      },
      {
        slug: 'mestiere',
        title: 'What I actually do at work',
        nativeTitle: 'Che lavoro faccio davvero',
        useWhen:
          'When "sono analista" has already been said and the other person keeps asking — what exactly you handle, who you deal with, what a day looks like, how you got there.',
        tags: ['work', 'career', 'small talk'],
        level: 4,
        blocks: [
        [
          'Sulla carta faccio l\'analista, ma in pratica mi occupo di far parlare tra loro sistemi che non erano nati per capirsi.',
          'On paper I\'m an analyst, but what I actually do is get systems talking to each other that were never built to.',
        ],
        [
          'Lavoro a stretto contatto con i colleghi dell\'ufficio vendite, che spesso non immaginano quanto lavoro ci sia dietro una richiesta apparentemente banale.',
          'I work closely with the people in sales, and they often have no idea how much work sits behind a request that looks trivial.',
          'After a negated verb of thinking — non immaginano, non sanno — the subjunctive is the more polished choice in an indirect question: quanto lavoro ci sia. The indicative, quanto lavoro c\'è, is also correct and is what most people say. After capire and sapere the indicative is the normal one: see block 5.',
        ],
        [
          'Una giornata tipo comincia con la riunione delle nove, poi passo gran parte del pomeriggio a scrivere codice senza che nessuno mi interrompa.',
          'A typical day starts with the nine o\'clock meeting, then I spend most of the afternoon writing code with nobody interrupting me.',
          'Senza che is a trigger you genuinely cannot dodge — interrompa, never interrompe — and it needs two different subjects (io … nessuno). Giornata tipo is invariable: giornate tipo in the plural, no preposition.',
        ],
        [
          'Ci sono finito per caso: avevo studiato tutt\'altro, poi mi hanno proposto un contratto a termine di tre mesi e alla fine sono rimasto.',
          'I fell into it by accident — I\'d studied something else entirely, then they offered me a three-month contract and I ended up staying.',
          'Finire + a place or a situation means to end up there, not to finish: sono finito in ospedale, è finito in banca. Here ci just stands in for the job, already mentioned, the way it does in ci vado — name the place and the ci disappears: sono finito in questo lavoro per caso.',
        ],
        [
          'La parte che mi piace di più non è la tecnologia in sé, ma il fatto di dover capire come lavora davvero la gente.',
          'The part I like best isn\'t the technology itself, it\'s having to work out how people really do their jobs.',
          'La gente is singular: la gente lavora, never lavorano. English speakers slip into the plural here more than anywhere else.',
        ],
        [
          'Se devo dirla tutta, la parte burocratica mi pesa parecchio, però il resto del lavoro è abbastanza vario da compensare.',
          'If I\'m being completely honest, the admin side wears on me, but the rest of the job is varied enough to make up for it.',
        ],
        ],
      },
      {
        slug: 'confronto',
        title: 'Here vs back home',
        nativeTitle: 'Qui e da noi',
        useWhen:
          'For the conversation every foreigner has over and over — what surprised you, what you\'ve got used to, and which country is better — when you want to answer with something more than \\"both are nice\\".',
        tags: ['expat life', 'comparing', 'opinions'],
        level: 4,
        blocks: [
        [
          'All\'inizio la cosa che mi ha colpito di più era che i negozi chiudevano il pomeriggio: per mesi mi sono ritrovato davanti alle saracinesche abbassate.',
          'What hit me hardest at first was the shops shutting in the afternoon — for months I kept turning up to find the shutters down.',
          'Habitual times take the bare article: il pomeriggio, la domenica, la sera. Nel pomeriggio would point at one particular afternoon.',
        ],
        [
          'Ormai mi sono abituato a fare la spesa quasi ogni giorno, e non tornerei indietro: si mangia meglio e si butta via molto meno.',
          'Shopping most days is second nature now, and I wouldn\'t go back — you eat better and throw far less away.',
          'Ormai is the word for a change that has already settled: "by now, after all this time." A woman says mi sono abituata — and ritrovata in the line above.',
        ],
        [
          'Quello che ancora mi spiazza è che qui la gente ti interrompe mentre parli, e non è maleducazione: è proprio così che funziona una conversazione.',
          'What still throws me is getting interrupted mid-sentence — it isn\'t rudeness here, it\'s just how a conversation works.',
          'That ti is the impersonal "you," not the person in front of you, so nothing here is aimed at your listener — the island works just as well with someone you address as Lei.',
        ],
        [
          'Si guadagna meno, su questo non si discute, però ho l\'impressione che nessuno ti guardi male se stacchi alle sei.',
          'The money\'s worse, no argument there, but nobody seems to give you a dirty look for knocking off at six.',
        ],
        [
          'Non è che qui si viva meglio che da noi: sono due modi diversi di organizzare la stessa vita, ognuno con i suoi difetti.',
          'It\'s not that life here is better than back home — they\'re two different ways of arranging the same life, each with its own downsides.',
          'Non è che takes the subjunctive: si viva, not si vive. (You will hear the indicative in casual speech; drill the subjunctive anyway, it holds up in any register.) And da noi is how you say "back home, where I\'m from" — a place, no possessive needed.',
        ],
        [
          'In fondo, dopo tutti questi anni non mi sento più straniero qui, ma quando torno dai miei non mi sento più a casa nemmeno lì.',
          'After all these years I don\'t feel foreign here any more — but when I go back to my parents\' I don\'t feel at home there either.',
        ],
        ],
      },
      {
        slug: 'disavventura',
        title: 'A small disaster',
        nativeTitle: 'Un piccolo disastro',
        useWhen:
          'When the conversation turns to how your week went and you want to tell the whole story of something that went wrong, not just report it in one sentence.',
        tags: ['storytelling', 'past tenses', 'mishaps'],
        level: 4,
        blocks: [
        [
          'Un paio di settimane fa stavo uscendo di corsa per non perdere il treno e mi sono tirato dietro la porta senza pensarci.',
          'Sets the scene: a couple of weeks ago I was rushing out for my train and pulled the door shut behind me without thinking.',
        ],
        [
          'Avevo fatto sì e no dieci passi quando ho capito che le chiavi erano rimaste sul tavolo della cucina, insieme al telefono.',
          'The moment it goes wrong — ten steps down the street and I realize the keys and my phone are on the kitchen table.',
          'Italian lets the keys do it: le chiavi erano rimaste, not avevo lasciato le chiavi. Rimanere takes essere, so the participle agrees with le chiavi — rimaste.',
        ],
        [
          'Lì per lì sono andato completamente nel pallone, perché avevo paura che il gas fosse ancora acceso sotto la caffettiera.',
          'The panic, with the reason for it: for a moment I lost my head, because the gas might still be on under the coffee pot.',
          'Avere paura che takes the subjunctive — fosse, never era. (Temere che would too, but temere is a written verb and would sit oddly against the slang in the same breath.) Andare nel pallone is losing your head, and it sets the friends-in-a-bar register the whole story runs in. A woman changes only the participles that point back at her — sono andata here, mi sono tirata in the first line.',
        ],
        [
          'Ho suonato a tutti i campanelli del palazzo finché una vicina non mi ha aperto il portone e mi ha prestato il telefono.',
          'What I tried first: ringing every buzzer in the building until a neighbor let me in and lent me her phone.',
          'The non in finché non is not a negative — Italian slips it in before the event that ends the waiting, and dropping it sounds off.',
        ],
        [
          'Alla fine è venuto un fabbro che ha aperto la porta in due minuti e mi ha chiesto centoventi euro in contanti.',
          'How it resolved, with the sting in the tail: the locksmith took two minutes and a hundred and twenty euros, cash.',
        ],
        [
          'Adesso il doppione delle chiavi ce l\'ha la vicina del piano di sotto, e prima di chiudere la porta mi tasto sempre le tasche.',
          'The moral: now the neighbor downstairs keeps a spare, and I check my pockets before the door shuts.',
          'Il doppione delle chiavi ce l\'ha la vicina puts the spare key first and doubles it with ce l\' — the everyday way to say who has what, far more natural here than La vicina ha il doppione. The verb for the checking gesture is tastarsi, not toccarsi.',
        ],
        ],
      },
      {
        slug: 'pressione',
        title: 'Work pressure',
        nativeTitle: 'Il lavoro e tutto il resto',
        useWhen:
          'When someone asks how work is going and you want the honest answer rather than the polite one — admitting the pressure, hedging it so you don\'t sound like you\'re complaining, and handing the question back.',
        tags: ['work', 'stress', 'small talk'],
        level: 5,
        blocks: [
        [
          'Guarda, in questo periodo sono parecchio sotto pressione, ma è una fase, o almeno è quello che continuo a dirmi.',
          'Look, I\'m under a lot of pressure right now, but it\'s a phase — or that\'s what I keep telling myself.',
        ],
        [
          'Non è tanto il carico di lavoro in sé, quanto il fatto che la sera non riesco a staccare.',
          'It\'s not so much the workload itself — it\'s that I can\'t switch off in the evening.',
          'Staccare (fuller: staccare la spina) is the verb for switching off from work; disconnettersi sounds like you\'re talking about a router. Non tanto… quanto concedes the obvious cause and then names the real one.',
        ],
        [
          'Nessuno mi obbliga a rispondere alle mail alle undici di sera: è sottinteso, il che è anche peggio.',
          'Nobody makes me answer emails at eleven at night — it\'s just taken as read, which is worse.',
        ],
        [
          'Da qualche mese ho cominciato a mettere dei paletti: dopo le otto il telefono resta in cucina.',
          'I\'ve started setting some limits these last few months — after eight, the phone stays in the kitchen.',
          'Mettere dei paletti is the colloquial, concrete way to say you\'ve drawn a line — paletti are the stakes you drive in to mark where something stops. Mettere dei limiti is also perfectly good Italian, just flatter; it\'s confini that would be a word-for-word calque of English "boundaries".',
        ],
        [
          'Non mi lamento, per carità, il lavoro mi piace: è che vorrei che non si prendesse anche tutto il resto.',
          'I\'m not complaining, honestly — I like my job. I just wish it didn\'t take everything else along with it.',
          'Per carità is a hedge, roughly "don\'t get me wrong" — it heads off being heard as a whiner, and has nothing to do with charity. Vorrei che then forces the imperfect subjunctive: si prendesse, never si prende.',
        ],
        [
          'Comunque ci sono periodi e periodi, eh. Tu invece riesci a staccare la sera?',
          'Anyway, some stretches are worse than others. Do you manage to switch off in the evenings?',
          'The doubled noun in ci sono periodi e periodi means "some are one way, some another". The island is tu throughout, and it surfaces in exactly two places: Guarda in line 1 and this question. On Lei they become Guardi, … and Lei invece riesce a staccare la sera? — change both or neither.',
        ],
        ],
      },
      {
        slug: 'tecnologia',
        title: 'AI and your job',
        nativeTitle: 'L\'intelligenza artificiale e il mio lavoro',
        useWhen:
          'When someone at dinner or at a conference asks whether AI is going to take your job, and you want to give a measured answer instead of a shrug.',
        tags: ['work', 'technology', 'opinions'],
        level: 5,
        blocks: [
        [
          'Guarda, il mio lavoro l\'intelligenza artificiale l\'ha già cambiato, ma non nel modo in cui se lo immagina la gente.',
          'Honestly, AI has already changed my job — just not in the way people imagine.',
          'Object first, subject second, and the clitic *l\'* copies the object back in: this dislocation is how the sentence actually gets said. Textbook order (*L\'intelligenza artificiale ha già cambiato il mio lavoro*) reads like a press release. *Guarda* is the tu imperative — with *Lei* it would be *guardi* — so this is talk between colleagues; nothing else in the island addresses the listener directly.',
        ],
        [
          'Tutta la parte ripetitiva — la prima stesura, la ricerca preliminare — ormai la fa la macchina in due minuti.',
          'The repetitive part — first drafts, background reading — the machine does that in two minutes now.',
          '*ormai* is not *adesso*. It means the shift has already happened and isn\'t going back; it carries the resignation that English gets from "at this point".',
        ],
        [
          'Quello che la macchina non può sostituire, però, è il giudizio: capire quando una risposta è plausibile e sbagliata insieme.',
          'What it can\'t take over, though, is judgment — spotting the answer that\'s plausible and wrong at the same time.',
          '*però* is mobile in a way *ma* is not — *ma* can only open the clause, while *però* can sit inside the sentence or at the end. Dropping it in after the subject, rather than starting with *ma*, is how the contrast actually gets made in speech. Naming *la macchina* also keeps the relative clause from being misheard: *sostituire* takes a direct object, so a bare *quello che non ti sostituisce* would land as "the thing that doesn\'t replace you".',
        ],
        [
          'Poi, per carità, non voglio fare il catastrofista: non è che il mestiere sparisca, è che si abbassano le tariffe.',
          'And look, I\'m not trying to be apocalyptic — the trade doesn\'t disappear, the rates drop.',
          '*non è che* takes the subjunctive (*sparisca*), the affirmative *è che* takes the indicative (*si abbassano*). The pair in one breath is the standard way to reject someone\'s framing and substitute your own.',
        ],
        [
          'Ai clienti, in ogni caso, devo rispondere io: se salta fuori un errore, la responsabilità non se la prende un algoritmo.',
          'Either way, I\'m the one who answers to the client — if something goes wrong, no algorithm takes the blame.',
          'The emphasis lives in the word order: *devo rispondere io*, subject pushed to the end. English needs a cleft ("I\'m the one who…") to do the same work.',
        ],
        [
          'Diciamo che tra cinque anni chi non la usa resterà indietro, ma chi la usa e basta non varrà granché.',
          'I\'d say in five years anyone who doesn\'t use it falls behind — and anyone who only uses it won\'t be worth much.',
          '*Diciamo che* softens a prediction you actually mean. *granché* only ever turns up under a negation — there is no positive use of it.',
        ],
        ],
      },
      {
        slug: 'riunione',
        title: 'Running a meeting',
        nativeTitle: 'In riunione',
        useWhen:
          'When you are the one holding the floor in an Italian work meeting — opening it, putting a proposal forward, saying the deadline will not hold, and closing — and none of it can sound blunt.',
        tags: ['work', 'meetings', 'formal'],
        level: 5,
        blocks: [
        [
          'Allora, intanto grazie per la disponibilità. Se ci siamo tutti, direi di cominciare.',
          'Right, thanks first of all for making the time. If we\'re all here, why don\'t we get started?',
          '*Direi di* starts the meeting without ordering anyone to start it — the conditional turns "we\'re beginning" into "shall we". *Intanto* here isn\'t "meanwhile" but "first off": one item cleared before the real business. The whole island is in the plural *voi*, the default when you address a room; one-to-one it would all shift to *Lei*. And *disponibilità* thanks people for making the time, not for being free.',
        ],
        [
          'Io proporrei di procedere per fasi, anziché consegnare tutto in un\'unica soluzione.',
          'I\'d suggest we go in stages rather than deliver it all at once.',
          'Proposals stay in the conditional: *propongo* is correct Italian, but *proporrei* offers the idea instead of announcing it, which is what you want before anyone has agreed. Italian normally drops subject pronouns, so the *io* is doing work: it marks this as my view, one of several on the table. *In un\'unica soluzione* is administrative language — its home is *pagamento in un\'unica soluzione* — which is why it fits a delivery schedule and nothing domestic.',
        ],
        [
          'Sulle tempistiche, invece, mi permetto un\'osservazione: il 30 mi sembra una scadenza difficilmente sostenibile.',
          'On the timeline, though — if I may, the 30th strikes me as a tough deadline to hold.',
          '*Mi permetto un\'osservazione* is the standard formal run-up to a disagreement: you announce the objection before you make it, so nobody is ambushed. *Invece* here isn\'t "instead" — it marks the turn to the sticking point, English "as for the timeline, though". *Tempistiche* is normally plural in office Italian, and *sostenibile* goes with *scadenza*; *una data sostenibile* is not a pairing Italian makes.',
        ],
        [
          'Se la consegna slittasse anche solo di due settimane, potrei garantirvi un risultato all\'altezza.',
          'If delivery slipped by even two weeks, I could guarantee you something up to standard.',
          '*Se* + imperfect subjunctive with the conditional (*slittasse … potrei*) floats a hypothesis; *se slitta … posso* treats the slip as a real prospect and lands as a flat trade — do this and I\'ll do that. *Slittare* puts *la consegna* in the subject slot and leaves the agent out altogether, so nobody has to be the person asking for an extension.',
        ],
        [
          'Naturalmente, se ci sono vincoli che mi sfuggono, ditemelo pure: preferisco saperlo adesso piuttosto che in corso d\'opera.',
          'Of course, if there are constraints I\'m not seeing, just say so — I\'d rather hear it now than halfway through.',
          '*In corso d\'opera* comes from building work and is now the ordinary office phrase for "once we\'re already underway". *Pure* after an imperative takes the edge off: "go ahead and tell me".',
        ],
        [
          'Direi che possiamo chiuderla qui: vi mando un riepilogo per iscritto e resto a disposizione per qualsiasi chiarimento.',
          'I think we can wrap up here — I\'ll send around a written summary, and I\'m happy to clarify anything.',
          '*Resto a disposizione* is the near-obligatory sign-off, in speech and email alike; in Italian it reads as plain courtesy, where a literal "I remain at your disposal" would sound arch in English. The *la* of *chiuderla* points at nothing in particular — the meeting, the matter — and the verb is simply fixed that way.',
        ],
        ],
      },
      {
        slug: 'posizione',
        title: 'Holding your ground',
        nativeTitle: 'Tenere il punto',
        useWhen:
          'When you\'re in a real disagreement with an Italian who has a fair point, and you need to grant it without conceding the argument.',
        tags: ['debate', 'hedging', 'opinion'],
        level: 6,
        blocks: [
        [
          'Non è che voglia fare il bastian contrario, ma continuo a pensare che il problema sia a monte.',
          'I\'m not just being contrary — I still think the problem is further upstream.',
        ],
        [
          'Che i numeri ti diano ragione, per carità, non lo discuto nemmeno.',
          'Look, the numbers are on your side, obviously — I\'m not going to argue with that.',
          '«Per carità» here is not pious. Dropped mid-sentence it concedes a point emphatically, right before you take it back. The island is held at tu, a discussion between equals; for lei only this line changes: «che i numeri Le diano ragione».',
        ],
        [
          'Ammesso e non concesso che sia così, il problema resta esattamente dov\'era.',
          'Even if we grant that for the sake of argument, it leaves the problem exactly where it was.',
          '«Ammesso e non concesso» is fixed, out of legal argument: it grants the premise for argument\'s sake while stating outright that you do not actually grant it. No English equivalent carries that second half in three words.',
        ],
        [
          'Sia chiaro, non sto dicendo che prima si stesse meglio: dico un\'altra cosa.',
          'Let me be clear, I\'m not saying things were better before. That\'s not my point.',
        ],
        [
          'Il punto, semmai, non è se funziona: è chi ne paga il prezzo.',
          'If anything, the real question isn\'t whether it works — it\'s who ends up paying for it.',
          '«Semmai» does two jobs. As a conjunction it does mean "if ever / in case", and takes the subjunctive: «semmai ti servisse, chiamami». Here it is the adverb — "if anything", close to «piuttosto», «tutt\'al più». What tells them apart is whether a clause follows, not where the word sits: it reads the same at the head of the sentence («Semmai, il punto non è se funziona»). Its job is to relocate the argument without contradicting what was just said.',
        ],
        [
          'Poi magari mi sbaglio, non ho la verità in tasca; ma finché non mi si dimostra il contrario, resto della mia idea.',
          'I could be wrong, I don\'t claim to have all the answers — but until someone shows me otherwise, I\'m sticking with it.',
          '«Restare della propria idea» is the fixed collocation; «resto con la mia idea» is not Italian. And the «non» in «finché non mi si dimostra» is expletive — it negates nothing.',
        ],
        ],
      },
      {
        slug: 'modidire',
        title: 'Idioms that land',
        nativeTitle: 'Modi di dire che si usano davvero',
        useWhen:
          'When you\'re talking shop with Italian colleagues who know you and want to concede a point, complain about carrying something alone, or close a subject the way a native would rather than the way a textbook would.',
        tags: ['idioms', 'register', 'spoken'],
        level: 6,
        blocks: [
        [
          'Guarda, alla fine della fiera ci ho messo una pezza io, come al solito.',
          'Look — when all was said and done, I was the one who patched it up, same as always.',
          'The whole island sits in one register: spoken, educated, among colleagues who know each other. "Alla fine della fiera" is the bar-and-open-plan-office cousin of "in fin dei conti" — identical meaning, but it would look wrong in anything written down. "Metterci una pezza" is a patch that holds for now, never a real fix, and the speaker knows it.',
        ],
        [
          'Mi sono fatto in quattro per chiudere quel contratto e sono rimasto con un pugno di mosche in mano.',
          'I bent over backwards to close that deal and walked away with nothing to show for it.',
          '"Restare con un pugno di mosche in mano" — a fistful of flies — is the standard image for effort that produced nothing; the "in mano" is optional but native speakers usually keep it. "Farsi in quattro" is effort well past what the job asked of you, usually on someone else\'s behalf, so saying it about yourself is a bid for credit — which is exactly what the flies then take away.',
        ],
        [
          'Il suo ragionamento non fa una piega, però mette sempre le mani avanti prima ancora di cominciare.',
          'His reasoning is airtight — but he\'s always covering himself before he\'s even started.',
          '"Non fa una piega" (not a single crease) concedes an argument you genuinely cannot fault, and it usually sets up a "però". "Mettere le mani avanti" is putting your hands out to break a fall: taking precautions so nothing can be pinned on you afterwards. On its own it is neutral, and you can say it about yourself — "metto le mani avanti: non sono un esperto". What turns it into a charge here is "sempre" plus the "però": you grant the point, then say he had his alibi ready before he started.',
        ],
        [
          'A quel punto ho fatto buon viso a cattivo gioco e ho lasciato perdere.',
          'At that point I made the best of a bad situation and dropped it.',
          'From card play — a good face over a bad hand, where "gioco" is the hand you were dealt. It concedes a situation you did not choose and do not like, so it always carries resignation; the bad hand can be a call from above or plain bad luck, and the phrase blames no one on its own. It\'s "ho lasciato perdere" that says you stopped fighting.',
        ],
        [
          'Una cosa però non mi va giù: che nessuno abbia detto niente.',
          'One thing still sticks in my throat, though — that nobody said a word.',
          '"Non mi va giù" is food that won\'t go down: resentment that hasn\'t settled, not disagreement. "Non sono d\'accordo" is the neutral word and much colder. It takes a person just as easily — "quel tipo non mi va giù" is I can\'t stand him, not I disagree with him.',
        ],
        [
          'Detto tra noi, se continua così io mi chiamo fuori e chi s\'è visto s\'è visto.',
          'Between us, if this keeps up I\'m out — and that\'s the end of it.',
          '"Chiamarsi fuori" is stepping back from something before it goes wrong, and keeping the fallout off you when it does; no English speaker guesses it from the words. "Chi s\'è visto s\'è visto" (whoever\'s been seen has been seen) slams the door: no follow-up, don\'t come back to me about it. Both are purely spoken — either one in an email reads as a joke or a threat.',
        ],
        ],
      },
  ],
}
